// ============================================================================
// Feuille « formules » jugée par le NOYAU (moteur v32, refonte lot 6.1 —
// docs/moteur/REFONTE_MOTEUR.md).
// ============================================================================
// Une question où l'on CALCULE une grandeur : des grandeurs données (N = 10),
// les formules de la SKILLCARD (S = N × (P + D) ÷ 2, Pythagore…), une ou plusieurs
// grandeurs à trouver. Toutes les grandeurs ont une valeur : c'est un « monde »
// fixé par l'énoncé. Chaque ligne de l'élève se juge en deux temps :
//
//   1. est-elle VRAIE dans ce monde (calcul exact, sinon à 10⁻⁹ près) ?
//   2. est-elle JUSTIFIÉE en un pas, depuis ce qui est déjà écrit :
//        - écrire une formule de la carte (valeurs déjà remplacées comprises) ;
//        - remplacer des grandeurs connues par leur valeur ;
//        - faire des calculs, un par endroit (« 15² » et « 9² » ensemble, mais pas
//          « 4 × 4 × 4 » d'un coup) ;
//        - une règle d'équation du noyau (même opération des deux côtés, racine…),
//          quand l'inconnue est prise dans une formule (Pythagore : BC² = 144) ;
//        - échanger les membres pour mettre l'inconnue à gauche.
//
// Décisions du lot 6 (D6 à D8) :
//   - une ligne JUSTE qui saute des étapes (deux calculs de tête, réponse directe,
//     remplacer ET calculer) reste sur la feuille, sans HORA (D7) ; elle compte
//     ensuite comme les autres (une valeur établie, un point de départ) ;
//   - « ≈ » est toujours proposé : une valeur arrondie écrite avec « = » guide vers
//     « ≈ » au lieu d'un « Calcul faux » ;
//   - une ligne peut s'effacer (la feuille se rejoue sans elle : rien ici n'est
//     gardé d'une ligne à l'autre hors de `lines`).
// ============================================================================

import { checkSyntax, formatNumber, lex, normalize, normalizeChips, type Chip } from './lexer';
import type { CheckSpec, ScratchModel, TrapSpec } from './model';
import { lineAtoms, parseLine, parseText, roundTo, type Line, type Node } from './tree';
import * as Q from './kernel/exact';
import {
  compareTerms,
  evalFloat,
  normalize as normalForm,
  rfDiv,
  rfRat,
  rfSimplify,
  rfToTerm,
  type RatFn,
} from './kernel/normal';
import {
  fromNode,
  num,
  parseStatement,
  substitute,
  symbolsOf,
  termKey,
  UnsupportedTerm,
  type Relation,
  type Statement,
  type Term,
} from './kernel/term';
import { checkAlgebraStatements } from './kernel/plugins/algebra';
import { kernelRules, kernelRuleStep } from './kernel-sheet';
import type { Condition, StepCheck } from './kernel/types';

/* ------------------------------ pas ------------------------------ */

/** Identifiants des pas d'une feuille à formules (`attempt_scratch_lines.rule_id`). */
export const FORMULA_STEP_IDS = {
  tool: 'formula.tool@1',
  replace: 'kernel.substitute@1',
  compute: 'kernel.compute@1',
  swap: 'kernel.swap@1',
  round: 'kernel.round@1',
  check: 'formula.check@1',
} as const;

export interface KForRule {
  id: string;
  card: string;
  label: string;
  hora: number;
  why: string;
}

export interface KForVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step'
    | 'dup'
    | 'given';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  trap?: { line: Line; title: string; why: string };
  rule?: KForRule;
  /** le noyau ne sait pas vérifier ce passage (valeur indécise) */
  unknown?: boolean;
  /** D7 : ligne juste qui saute des étapes, gardée sans HORA */
  jump?: boolean;
  /** ce que la ligne apporte à la feuille (lu par `kforCommit`) */
  kfor?: KForCommit;
}

interface KForCommit {
  /** relations établies par la ligne (membre de tête = chaque membre, maillons) */
  rels: Rel2[];
  /** la ligne est vraie : elle sert de point de départ */
  trusted: boolean;
  /** ligne approchée (« AC ≈ 8,4 ») : jamais une source, sa valeur arrondie */
  approx?: { name: string | null; value: Term };
  /** comparaison décisive écrite (`checks` du modèle) */
  check?: string;
  /** formule de la carte écrite (son indice) */
  tool?: number;
}

/** Une égalité entre deux membres (jamais « ≈ »). */
interface Rel2 {
  l: Term;
  r: Term;
}

interface Tool {
  rel: Rel2;
  text: string;
  label: string;
  hora: number;
  why: string;
  hint: string;
  card: string;
  /** grandeurs de la formule */
  names: Set<string>;
  /** forme normale de « l − r » (comparaison à un facteur près) */
  rf: RatFn | null;
}

interface FTrap {
  rel: Rel2;
  line: Line;
  title: string;
  why: string;
  rf: RatFn | null;
  key: string;
}

interface Fact extends Rel2 {
  key: string;
  /** énoncé (valeur donnée, égalité affichée, résultat admis) */
  given: boolean;
}

interface WorldValue {
  /** valeur exacte (terme sans lettre), null : seulement approchée */
  exact: Term | null;
  f: number;
}

export interface KForLine {
  rels: Rel2[];
  trusted: boolean;
  approx?: { name: string | null; value: Term };
  tool?: number;
}

export interface KForState {
  model: ScratchModel;
  goals: string[];
  answer: Statement | null;
  /** valeur de chaque grandeur */
  W: Record<string, WorldValue>;
  Wf: Record<string, number>;
  given: Fact[];
  tools: Tool[];
  traps: FTrap[];
  checks: { spec: CheckSpec; rel: Relation; key: string }[];
  hyps: Condition[];
  path: string[];
  lines: KForLine[];
  /** comparaisons décisives déjà écrites */
  checked: Set<string>;
  /** atomes des lignes du corrigé et de la réponse (en cours de frappe : « building ») */
  known?: string[][];
}

/* ------------------------------ lecture ------------------------------ */

interface FLine {
  /** membres de la chaîne (au moins un) */
  members: Term[];
  /** comparaison (« Δ > 0 ») : relation entre les deux membres ; sinon des « = » */
  cmp: '>' | '<' | '≥' | '≤' | null;
  /** valeur approchée écrite après « ≈ » */
  approx: Term | null;
  line: Line;
}

class Unreadable extends Error {}

/** Les atomes d'une ligne de texte (« AC = √70 ≈ 8,37 » compris). */
export function kforLineAtoms(text: string): string[] {
  return normalize(lex(text));
}

function readAtoms(A: string[]): FLine {
  const ax = A.lastIndexOf('≈');
  // « … = v ≈ w » : la chaîne, puis la valeur approchée ; « S ≈ 3,1 » : un seul membre
  const chain = ax >= 0 ? A.slice(0, ax) : A;
  const after = ax >= 0 ? A.slice(ax + 1) : [];
  if (ax >= 0 && (!chain.length || !after.length)) throw new Unreadable();
  if (chain.includes('≈') || chain.includes('ou')) throw new Unreadable();
  const hasEq = chain.includes('=');
  let line: Line;
  let members: Term[];
  let cmp: FLine['cmp'] = null;
  try {
    if (hasEq || ax < 0) {
      line = parseLine(chain);
      if (line.dis.length !== 1) throw new Unreadable();
      const eq = line.dis[0]!;
      if (eq.rels || eq.mod !== undefined || eq.chain) throw new Unreadable();
      if (eq.cmp === '≈') throw new Unreadable();
      if (eq.cmp) {
        if (ax >= 0) throw new Unreadable();
        cmp = eq.cmp;
      }
      members = eq.sides.map(fromNode);
    } else {
      // « AC ≈ 8,4 » : un seul membre avant « ≈ »
      line = parseLine([...chain, '≈', ...after]);
      const eq = line.dis[0]!;
      members = [fromNode(eq.sides[0]!)];
    }
  } catch (e) {
    if (e instanceof Unreadable || e instanceof UnsupportedTerm) throw new Unreadable();
    throw new Unreadable();
  }
  let approx: Term | null = null;
  if (ax >= 0) {
    try {
      const p = parseLine(after);
      const eq = p.dis[0]!;
      if (p.dis.length !== 1 || eq.sides.length !== 1) throw new Unreadable();
      approx = fromNode(eq.sides[0]!);
    } catch {
      throw new Unreadable();
    }
    if (hasEq) {
      // la ligne affichée garde « ≈ » : on la relit par son texte
      try {
        line = parseText(A.join(' ').replace(/≈.*$/, '').trim());
      } catch {
        throw new Unreadable();
      }
    }
  }
  if (!members.length || (cmp === null && members.length < 2 && !approx)) throw new Unreadable();
  return { members, cmp, approx, line };
}

function readText(text: string): FLine | null {
  try {
    return readAtoms(kforLineAtoms(text));
  } catch {
    return null;
  }
}

/* ------------------------------ formes ------------------------------ */

const isNumT = (t: Term): t is { k: 'num'; v: Q.Rat } => t.k === 'num';

/** Le terme n'écrit aucune grandeur (π et les nombres seulement). */
function isConstTerm(t: Term): boolean {
  return symbolsOf(t).size === 0;
}

/** « −x » écrit comme un produit par −1 : ce n'est pas une opération à faire. */
function isNegation(t: Term): boolean {
  // « −1 × 4 » écrit en toutes lettres est un produit ; « −p » est un signe
  return (
    t.k === 'mul' &&
    t.c.length === 2 &&
    isNumT(t.c[0]!) &&
    Q.eq(t.c[0]!.v, Q.rat(-1)) &&
    !isNumT(t.c[1]!)
  );
}

/** Une valeur irrationnelle écrite d'un bloc : √7, ln(3), exp(1), cos(40°) (rien à calculer). */
function isAtomicValue(t: Term): boolean {
  if (t.k !== 'fn' || t.f === 'deg' || t.f === 'abs' || t.f === 'fact') return false;
  const arg = t.a.k === 'fn' && t.a.f === 'deg' ? t.a.a : t.a;
  if (!isNumT(arg) && !isIrreducibleFraction(arg)) return false;
  const n = normalForm(t);
  return !(n.ok && rfRat(n.rf));
}

/** Une fraction écrite (« 5/8 », « −1/4 ») plutôt qu'un décimal. */
function isFractionWritten(t: Term): boolean {
  if (isNegation(t)) return isFractionWritten((t as { c: Term[] }).c[1]!);
  return isIrreducibleFraction(t);
}

/** π, √7, ln(3)… : une valeur irrationnelle écrite d'un bloc. */
function isIrrationalAtom(t: Term): boolean {
  return (t.k === 'sym' && t.n === 'π') || isAtomicValue(t);
}

/** Le nombre rationnel écrit (« 3 », « −0,2 », « 5 ÷ 6 »), null sinon. */
function ratOf(t: Term): Q.Rat | null {
  if (isNumT(t)) return t.v;
  if (isIrreducibleFraction(t)) {
    const d = t as { a: { v: Q.Rat }; b: { v: Q.Rat } };
    return Q.div(d.a.v, d.b.v);
  }
  if (isNegation(t)) {
    const r = ratOf((t as { c: Term[] }).c[1]!);
    return r ? Q.neg(r) : null;
  }
  return null;
}

/** Nombre d'opérations écrites (« 10 × (1 + 19) ÷ 2 » : 3). */
function opsOf(t: Term): number {
  // « 5 ÷ 6 » : une fraction irréductible est une valeur, pas un calcul à faire ; √7 aussi
  if (isIrreducibleFraction(t) || isAtomicValue(t)) return 0;
  switch (t.k) {
    case 'num':
    case 'sym':
      return 0;
    case 'add':
      return t.c.length - 1 + t.c.reduce((s, c) => s + opsOf(c), 0);
    case 'mul': {
      if (isNegation(t)) return opsOf(t.c[1]!);
      // « 18π », « 2√3 » : un coefficient devant une valeur irrationnelle n'est pas un calcul
      // (« π × 0 » en est un)
      const atoms = t.c.filter(isIrrationalAtom).length;
      const coef = (c: Term) => {
        const q = ratOf(c);
        return q !== null && !Q.isZero(q);
      };
      const free = atoms === 1 && t.c.some(coef) ? 1 : 0;
      return t.c.length - 1 - free + t.c.reduce((s, c) => s + opsOf(c), 0);
    }
    case 'div':
      // « π ÷ 3 », « √2 ÷ 2 » : une valeur irrationnelle divisée par un nombre
      if (isIrrationalAtom(t.a) && ratOf(t.b) !== null) return 0;
      return 1 + opsOf(t.a) + opsOf(t.b);
    case 'pow':
    case 'binom':
      return 1 + opsOf(t.a) + opsOf(t.b);
    case 'fn':
      // « 60° » n'est pas un calcul à faire
      return (t.f === 'deg' ? 0 : 1) + opsOf(t.a);
  }
}

/** Clé de FORME : sommes et produits triés (l'ordre ne compte pas), nombres tels qu'écrits. */
function shapeKey(t: Term): string {
  switch (t.k) {
    case 'num':
      return Q.ratToText(t.v);
    case 'sym':
      return t.n;
    case 'add':
    case 'mul':
      return `${t.k}(${t.c.map(shapeKey).sort().join(',')})`;
    case 'div':
    case 'pow':
    case 'binom':
      return `${t.k}(${shapeKey(t.a)},${shapeKey(t.b)})`;
    case 'fn':
      return `${t.f}(${shapeKey(t.a)})`;
  }
}
function relKey(r: Rel2): string {
  return shapeKey(r.l) + '=' + shapeKey(r.r);
}
function relKeys(r: Rel2): [string, string] {
  return [relKey(r), shapeKey(r.r) + '=' + shapeKey(r.l)];
}

/* ------------------------------ monde ------------------------------ */

function exprOf(text: string): Term | null {
  const p = parseStatement(text);
  return p.ok && p.s.k === 'expr' ? p.s.e : null;
}

/** Un décimal court (≤ 6 chiffres après la virgule) : exact ; sinon seulement approché. */
function exactOfFloat(v: number): Term | null {
  if (!Number.isFinite(v)) return null;
  const r = Math.round(v * 1e6) / 1e6;
  if (Math.abs(r - v) > 1e-12 * Math.max(1, Math.abs(v))) return null;
  const q = Q.parseDecimal(String(r));
  return q ? num(q) : null;
}

/** Les valeurs du monde remplacées dans un terme (null : une grandeur sans valeur exacte). */
function exactIn(ks: KForState, t: Term): Term | null {
  const map: Record<string, Term> = {};
  for (const n of symbolsOf(t)) {
    const w = ks.W[n];
    if (!w?.exact) return null;
    map[n] = w.exact;
  }
  return substitute(t, map);
}

/** Valeur approchée d'un terme dans le monde (NaN : indéfinie ou grandeur inconnue). */
function floatOf(ks: KForState, t: Term): number {
  return evalFloat(t, ks.Wf);
}

/** Deux termes ont-ils la même valeur dans le monde ? null : indécis. */
function sameValue(ks: KForState, a: Term, b: Term): boolean | null {
  const A = exactIn(ks, a);
  const B = exactIn(ks, b);
  if (A && B) {
    const c = compareTerms(A, B);
    if (c === 'equal') return true;
    if (c === 'different') return false;
  }
  const fa = floatOf(ks, a);
  const fb = floatOf(ks, b);
  if (!Number.isFinite(fa) || !Number.isFinite(fb)) return null;
  return Math.abs(fa - fb) <= 1e-9 * Math.max(1, Math.abs(fa), Math.abs(fb));
}

/** Une comparaison est-elle vraie dans le monde ? null : indécis. */
function cmpHolds(ks: KForState, rel: '>' | '<' | '≥' | '≤', a: Term, b: Term): boolean | null {
  const fa = floatOf(ks, a);
  const fb = floatOf(ks, b);
  if (!Number.isFinite(fa) || !Number.isFinite(fb)) return null;
  const eq = sameValue(ks, a, b);
  if (eq === true) return rel === '≥' || rel === '≤';
  if (eq === null && Math.abs(fa - fb) < 1e-9) return null;
  return rel === '>' || rel === '≥' ? fa > fb : fa < fb;
}

/* ------------------------------ état ------------------------------ */

function relFromText(text: string): Rel2 | null {
  const f = readText(text);
  if (!f || f.cmp || f.approx || f.members.length < 2) return null;
  return { l: f.members[0]!, r: f.members[f.members.length - 1]! };
}

/** « l − r » en forme normale (null : hors d'atteinte). */
function diffRf(r: Rel2): RatFn | null {
  const n = normalForm({ k: 'add', c: [r.l, { k: 'mul', c: [num(-1), r.r] }] });
  return n.ok ? n.rf : null;
}

/** Deux relations disent-elles la même chose, à un facteur près (« BC² = AB² − AC² ») ? */
function proportional(a: RatFn | null, b: RatFn | null): boolean {
  if (!a || !b) return false;
  try {
    const q = rfSimplify(rfDiv(a, b));
    const k = rfRat(q);
    return !!k && !Q.isZero(k);
  } catch {
    return false;
  }
}

function pythagoreTools(model: ScratchModel): {
  tools: Omit<Tool, 'rel' | 'names' | 'rf'>[];
  traps: TrapSpec[];
} {
  const tools: Omit<Tool, 'rel' | 'names' | 'rf'>[] = [];
  const traps: TrapSpec[] = [];
  for (const p of model.preds ?? []) {
    if (p.skill === 'formule')
      tools.push({
        text: p.line,
        label: p.label || 'Formule',
        hora: p.hora || 3,
        why: p.why || 'On applique la formule : ' + p.line + '.',
        hint: p.hint || 'Utilise la formule : ' + p.line + '.',
        card: 'Formule',
      });
    else if (p.skill === 'pythagore') {
      const [a, b] = p.sides;
      const h = p.hyp;
      tools.push({
        text: `${h}² = ${a}² + ${b}²`,
        label: 'Pythagore',
        hora: 5,
        why: `Le triangle est rectangle en ${p.right}, donc [${h}] est l’hypoténuse : ${h}² = ${a}² + ${b}².`,
        hint: `Le triangle est rectangle en ${p.right} : quel théorème relie ses trois côtés ? Commence la ligne par ${h}².`,
        card: 'Théorème de Pythagore',
      });
      const hypo = `L’angle droit est en ${p.right} : l’hypoténuse est le côté opposé, [${h}]. C’est ${h}² qui est seul d’un côté.`;
      const plus =
        'Pour obtenir le carré de l’hypoténuse, on ADDITIONNE les carrés des deux autres côtés.';
      traps.push(
        {
          line: `${h} = ${a} + ${b}`,
          title: 'Additionner les longueurs',
          why: `Pythagore relie les CARRÉS des longueurs : ${h}² = ${a}² + ${b}².`,
        },
        {
          line: `${h}² = ${a} + ${b}`,
          title: 'Il manque les carrés',
          why: `Ce sont les carrés des côtés qui s’additionnent : ${a}² + ${b}².`,
        },
        { line: `${a}² = ${h}² + ${b}²`, title: 'Mauvaise hypoténuse', why: hypo },
        { line: `${b}² = ${h}² + ${a}²`, title: 'Mauvaise hypoténuse', why: hypo },
        { line: `${h}² = ${b}² − ${a}²`, title: 'Soustraire au lieu d’additionner', why: plus },
        { line: `${h}² = ${a}² − ${b}²`, title: 'Soustraire au lieu d’additionner', why: plus },
      );
    } else if (p.skill === 'baisse') {
      const base = typeof p.base === 'number' ? formatNumber(p.base) : p.base;
      const t = formatNumber(p.taux);
      tools.push(
        {
          text: `${p.remise} = ${t} % × ${base}`,
          label: 'Pourcentage',
          hora: 4,
          why: `Baisser de ${t} %, c’est retirer ${t} % du prix : la remise vaut ${t} % de ${base}.`,
          hint: `Commence par la remise : combien font ${t} % de ${base} ?`,
          card: 'Appliquer un pourcentage',
        },
        {
          text: `${p.nouveau} = ${base} − ${p.remise}`,
          label: 'Retirer la remise',
          hora: 3,
          why: 'Le nouveau prix, c’est le prix de départ moins la remise.',
          hint: 'Le nouveau prix, c’est le prix de départ moins la remise.',
          card: 'Appliquer un pourcentage',
        },
        {
          text: `${p.nouveau} = ${base} × (1 − ${t} %)`,
          label: 'Coefficient multiplicateur',
          hora: 5,
          why: `Baisser de ${t} %, c’est garder ${formatNumber(100 - p.taux)} % du prix : on multiplie par 1 − ${t} %.`,
          hint: `Baisser de ${t} %, c’est multiplier le prix par un nombre. Lequel ?`,
          card: 'Appliquer un pourcentage',
        },
      );
      traps.push(
        {
          line: `${p.nouveau} = ${base} − ${t}`,
          title: `−${t} % n’est pas −${t}`,
          why: `${t} % de ${base} ne font pas ${t} : la remise se calcule sur le prix (${t} % × ${base}).`,
        },
        {
          line: `${p.nouveau} = ${t} % × ${base}`,
          title: 'Ça, c’est la remise',
          why: `${t} % × ${base} est le montant de la réduction, pas le nouveau prix. Il faut encore le retirer du prix de départ.`,
        },
      );
    } else if (p.skill === 'secondDegre') {
      tools.push({
        text: `${p.delta} = ${p.b}² − 4 × ${p.a} × ${p.c}`,
        label: 'Discriminant',
        hora: 5,
        why: 'Pour ax² + bx + c = 0, on calcule le discriminant : Δ = b² − 4ac.',
        hint: 'Commence par le discriminant : Δ = b² − 4ac.',
        card: 'Équation du second degré',
      });
      const roots: [string, '−' | '+'][] = [
        [p.roots[0], '−'],
        [p.roots[1], '+'],
      ];
      roots.forEach(([r, s]) =>
        tools.push({
          text: `${r} = (−${p.b} ${s} √${p.delta}) ÷ (2 × ${p.a})`,
          label: 'Formule des racines',
          hora: 5,
          why: `Δ > 0, donc deux solutions : ${p.roots[0]} = (−b − √Δ) ÷ (2a) et ${p.roots[1]} = (−b + √Δ) ÷ (2a).`,
          hint: `Δ est positif : il y a deux solutions. Écris ${r} = (−b ${s} √Δ) ÷ (2a).`,
          card: 'Équation du second degré',
        }),
      );
      if (p.double)
        tools.push({
          text: `${p.double} = −${p.b} ÷ (2 × ${p.a})`,
          label: 'Solution double',
          hora: 5,
          why: `Δ = 0, donc une seule solution : ${p.double} = −b ÷ (2a).`,
          hint: `Δ est nul : il y a une seule solution. Écris ${p.double} = −b ÷ (2a).`,
          card: 'Équation du second degré',
        });
    }
  }
  return { tools, traps };
}

/** Lettres que le modèle déclare (`symbols`) : toute autre lettre est inconnue de la feuille. */
function declared(model: ScratchModel): Set<string> {
  return new Set(Object.keys(model.symbols));
}

export class FormulaModelError extends Error {}

export function createKForState(model: ScratchModel): KForState {
  const spec = model.kernel!;
  const ks: KForState = {
    model,
    goals: [...model.goal],
    answer: null,
    W: {},
    Wf: {},
    given: [],
    tools: [],
    traps: [],
    checks: [],
    hyps: [],
    path: spec.path ?? [],
    lines: [],
    checked: new Set(),
  };
  const setW = (n: string, exact: Term | null, f: number) => {
    ks.W[n] = { exact, f };
    ks.Wf[n] = f;
  };
  // 1. valeurs données et valeurs exactes du modèle
  for (const [n, s] of Object.entries(model.symbols)) {
    const ex = s.exact?.length ? exprOf(s.exact[0]!) : null;
    if (ex && isConstTerm(ex)) {
      setW(n, ex, evalFloat(ex, {}));
      continue;
    }
    const v = s.value ?? (s.values?.length === 1 ? s.values[0] : undefined);
    if (v !== undefined) setW(n, exactOfFloat(v), v);
  }
  // 2. valeurs exactes tirées du corrigé : « X = expression sans inconnue »
  const pathRels = ks.path.flatMap((t) => {
    const f = readText(t);
    return f && !f.cmp && f.members.length >= 2 ? [f] : [];
  });
  for (let round = 0; round < 4; round++) {
    let changed = false;
    for (const f of pathRels) {
      const head = f.members[0]!;
      if (head.k !== 'sym' || ks.W[head.n]?.exact) continue;
      for (const m of f.members.slice(1).reverse()) {
        if (symbolsOf(m).has(head.n)) continue;
        const e = exactIn(ks, m);
        if (!e) continue;
        const n = normalForm(e);
        const exact = n.ok && rfRat(n.rf) ? num(rfRat(n.rf)!) : e;
        const fv = evalFloat(e, {});
        if (!Number.isFinite(fv)) continue;
        // une valeur approchée déjà connue doit s'accorder
        const prev = ks.W[head.n];
        if (prev && Math.abs(prev.f - fv) > 1e-6 * Math.max(1, Math.abs(fv))) continue;
        setW(head.n, exact, fv);
        changed = true;
        break;
      }
    }
    if (!changed) break;
  }
  const missing = [...declared(model)].filter((n) => !(n in ks.Wf));
  if (missing.length) throw new FormulaModelError(`grandeur sans valeur : ${missing.join(', ')}`);

  // 3. réponse
  const ans = parseStatement(spec.answer);
  if (ans.ok) ks.answer = ans.s;

  // 4. énoncé : valeurs données, égalités affichées, résultats admis
  const pushGiven = (r: Rel2) => {
    const key = relKey(r);
    if (!ks.given.some((g) => g.key === key)) ks.given.push({ ...r, key, given: true });
  };
  for (const [n, s] of Object.entries(model.symbols)) {
    if (!s.given) continue;
    const w = ks.W[n]!;
    const v = w.exact ?? exactOfFloat(w.f);
    if (v) pushGiven({ l: { k: 'sym', n }, r: v });
  }
  for (const t of [...(model.givens ?? []), ...(model.admitted ?? [])]) {
    const f = readText(t);
    if (!f || f.cmp || f.members.length < 2) continue;
    for (let i = 1; i < f.members.length; i++) pushGiven({ l: f.members[0]!, r: f.members[i]! });
  }

  // 5. formules de la carte et pièges
  const { tools, traps } = pythagoreTools(model);
  for (const t of tools) {
    const rel = relFromText(t.text);
    if (!rel) continue;
    const names = new Set([...symbolsOf(rel.l), ...symbolsOf(rel.r)]);
    ks.tools.push({ ...t, rel, names, rf: diffRf(rel) });
  }
  for (const t of [...(model.traps ?? []), ...traps]) {
    const rel = relFromText(t.line);
    if (!rel) continue;
    let line: Line;
    try {
      line = parseText(t.line);
    } catch {
      continue;
    }
    // le piège à valeurs données remplacées : on le reconnaît aussi rempli
    ks.traps.push({
      rel,
      line,
      title: t.title,
      why: t.why,
      rf: diffRf(withGivenValues(ks, rel)),
      key: relKey(rel),
    });
  }
  for (const c of model.checks ?? []) {
    const f = readText(c.line);
    if (!f || !f.cmp) continue;
    ks.checks.push({
      spec: c,
      rel: { rel: f.cmp, l: f.members[0]!, r: f.members[1]! },
      key: `${shapeKey(f.members[0]!)}${f.cmp}${shapeKey(f.members[1]!)}`,
    });
  }

  // 6. signes connus : servent aux règles d'équation (prendre la racine d'une longueur)
  for (const n of Object.keys(model.symbols)) {
    const f = ks.Wf[n]!;
    if (f > 0) ks.hyps.push({ kind: 'positive', of: [n] });
    else if (f < 0) ks.hyps.push({ kind: 'negative', of: [n] });
    if (model.symbols[n]!.kind === 'int' && Number.isInteger(f))
      ks.hyps.push({ kind: 'integer', of: [n] });
  }
  return ks;
}

/** La relation avec les grandeurs DONNÉES remplacées par leur valeur. */
function withGivenValues(ks: KForState, r: Rel2): Rel2 {
  const map: Record<string, Term> = {};
  for (const [n, s] of Object.entries(ks.model.symbols)) {
    if (!s.given) continue;
    const w = ks.W[n];
    if (w?.exact) map[n] = w.exact;
  }
  return { l: substitute(r.l, map), r: substitute(r.r, map) };
}

/* ------------------------------ connaissances ------------------------------ */

/** Toutes les relations de confiance : énoncé, puis lignes (la plus récente d'abord). */
function facts(ks: KForState): Fact[] {
  const out: Fact[] = [];
  for (let i = ks.lines.length - 1; i >= 0; i--) {
    const l = ks.lines[i]!;
    if (!l.trusted) continue;
    for (const r of l.rels) out.push({ ...r, key: relKey(r), given: false });
  }
  out.push(...ks.given);
  return out;
}

/** Une valeur écrite sans calcul en attente (« 100 », « 5π ÷ 6 », « √70 »). */
function isValueForm(t: Term): boolean {
  if (!isConstTerm(t)) return false;
  if (opsOf(t) === 0) return true;
  if (hasLeafOp(t)) return false;
  // « 10√2 − 6√2 » se réduit encore ; « 5 × π ÷ 6 » est écrit (à une opération près)
  return opsOf(t) <= canonicalOps(t) + 1;
}
function canonicalOps(t: Term): number {
  const n = normalForm(t);
  if (!n.ok) return opsOf(t);
  try {
    return opsOf(rfToTerm(n.rf));
  } catch {
    return opsOf(t);
  }
}

/** Grandeurs dont la valeur est connue : données, ou établies (« X = 100 »). */
function knownNames(ks: KForState): Set<string> {
  const out = new Set<string>();
  for (const [n, s] of Object.entries(ks.model.symbols)) if (s.given) out.add(n);
  const all = facts(ks);
  for (const f of all) {
    if (f.l.k === 'sym' && isValueForm(f.r)) out.add(f.l.n);
    if (f.r.k === 'sym' && isValueForm(f.l)) out.add(f.r.n);
  }
  // « E(X) = p » et p donné : E(X) est connu aussi
  for (let round = 0; round < 3; round++)
    for (const f of all) {
      if (f.l.k === 'sym' && f.r.k === 'sym') {
        if (out.has(f.r.n)) out.add(f.l.n);
        if (out.has(f.l.n)) out.add(f.r.n);
      }
    }
  return out;
}

/* ------------------------------ un pas sur un membre ------------------------------ */

interface Ev {
  /** remplacée par une expression établie (« a = 2 − b ») plutôt que par sa valeur */
  expr?: boolean;
  replace: string[];
  leaf: number;
  /** développer un produit par une somme (« 3(x + 1) » → « 3x + 3 × 1 ») */
  expand: number;
  /** calculs enchaînés d'un coup, ou forme qui recule (dé-calculer) */
  deep: number;
  reorder: boolean;
}
const NONE: Ev = { replace: [], leaf: 0, expand: 0, deep: 0, reorder: false };
function merge(a: Ev, b: Ev): Ev {
  return {
    ...(a.expr || b.expr ? { expr: true } : {}),
    replace: [...a.replace, ...b.replace],
    leaf: a.leaf + b.leaf,
    expand: a.expand + b.expand,
    deep: a.deep + b.deep,
    reorder: a.reorder || b.reorder,
  };
}
const cost = (e: Ev): number =>
  e.deep * 100 + e.replace.length * 3 + e.leaf * 2 + e.expand * 2 + +e.reorder;

/**
 * « − 2 × 6 » (lu −(2 × 6)) et « (−2) × 6 » : le même produit signé. Le signe passe au
 * premier nombre du produit, pour que remplacer b = −2 dans « b × 6 » s'écrive comme
 * l'élève l'écrit dans une somme (« 3 × 4 − 2 × 6 »).
 */
function foldNeg(t: Term): Term {
  if (!isNegation(t)) return t;
  const inner = (t as { c: Term[] }).c[1]!;
  if (inner.k !== 'mul' || isNegation(inner) || !isNumT(inner.c[0]!)) return t;
  return { k: 'mul', c: [num(Q.neg(inner.c[0].v)), ...inner.c.slice(1)] };
}

/** Le passage du membre `a` au membre `b`, par remplacements et calculs (null : impossible). */
function memberMove(ks: KForState, a: Term, b: Term, depth = 0): Ev | null {
  const raw = memberMoveRaw(ks, a, b, depth);
  if (raw && !raw.deep) return raw;
  // « b × d » → « − 2 × 6 » (b = −2) : le signe lu avec le premier nombre du produit
  const fa = foldNeg(a);
  const fb = foldNeg(b);
  if (fa === a && fb === b) return raw;
  const folded = memberMoveRaw(ks, fa, fb, depth);
  if (!folded) return raw;
  return !raw || cost(folded) < cost(raw) ? folded : raw;
}

function memberMoveRaw(ks: KForState, a: Term, b: Term, depth: number): Ev | null {
  if (shapeKey(a) === shapeKey(b))
    return termKey(a) === termKey(b) ? NONE : { ...NONE, reorder: true };
  if (depth > 40) return null;
  if (depth === 0) {
    const sub = substitutionMove(ks, a, b);
    if (sub) return sub;
  }
  // « − p » → « − 0,2 » : le signe se lit avec le nombre
  if (isNegation(a) && isNumT(b))
    return memberMove(ks, (a as { c: Term[] }).c[1]!, num(Q.neg(b.v)), depth + 1);
  if (isNumT(a) && isNegation(b))
    return memberMove(ks, num(Q.neg(a.v)), (b as { c: Term[] }).c[1]!, depth + 1);
  // une grandeur remplacée par sa valeur, ou par une expression établie (substitution)
  if (a.k === 'sym') {
    if (isConstTerm(b) && sameValue(ks, a, b)) return { ...NONE, replace: [a.n] };
    if (!isConstTerm(b) && expressionsOf(ks, a.n).some((e) => shapeKey(e) === shapeKey(b)))
      return { ...NONE, replace: [a.n], expr: true };
    return null;
  }
  const aConst = isConstTerm(a);
  const bConst = isConstTerm(b);
  // même opération : les opérandes un à un
  const structural = sameShapeMove(ks, a, b, depth);
  if (structural) return structural;
  // un calcul sur des nombres (« (0 + 0 + 2) × 1 » → « 0 + 0 + 2 ») : pas un développement
  if (aConst && bConst && opsOf(a) - opsOf(b) === 1 && sameValue(ks, a, b))
    return { ...NONE, leaf: 1 };
  // développer : « k × (u + v) » → « k × u + k × v »
  const ex = expandMove(ks, a, b, depth);
  if (ex) return ex;
  // réduire : « 5√2 + 3√2 » → « 8√2 », « 2y + 3y » → « 5y »
  if (likeTerms(a, b)) return { ...NONE, leaf: 1 };
  // enlever des parenthèses : « 12 − (3 + 5) » → « 12 − 3 − 5 »
  if (a.k === 'add' && b.k === 'add' && b.c.length > a.c.length) {
    const open = flatten(openNegated(a));
    if (shapeKey(open) === shapeKey(flatten(b))) return { ...NONE, expand: 1 };
  }
  if (aConst && bConst) {
    if (!sameValue(ks, a, b)) return null;
    const done = opsOf(a) - opsOf(b);
    if (done === 1) return { ...NONE, leaf: 1 };
    // une fraction écrite en décimal (« 5/8 » → « 0,625 ») : une division à faire
    if (done === 0 && isFractionWritten(a) !== isFractionWritten(b) && ratOf(a) && ratOf(b))
      return { ...NONE, leaf: 1 };
    // même valeur, autant d'opérations : écrite autrement
    if (done === 0) return { ...NONE, reorder: true };
    // un calcul de trop (« 4 × 4 × 4 » → « 64 »), ou une écriture qui recule (144 → 12²)
    return { ...NONE, deep: Math.max(1, Math.abs(done)) };
  }
  // avec une inconnue : « 10 × 20 ÷ 2 × x » → « 100x » (réassocié)
  const sa = [...symbolsOf(a)].sort().join(',');
  const sb = [...symbolsOf(b)].sort().join(',');
  if (sa === sb && sameExpr(a, b)) {
    const done = opsOf(a) - opsOf(b);
    if (done === 1) return { ...NONE, leaf: 1 };
    if (done <= 0) return { ...NONE, reorder: true, deep: done < 0 ? 1 : 0 };
    return { ...NONE, deep: done };
  }
  return null;
}

/** « −(u + v) » dans une somme → « −u − v » (les signes changent). */
function openNegated(t: Term): Term {
  if (t.k !== 'add') return t;
  const c: Term[] = [];
  for (const x of t.c) {
    if (isNegation(x) && (x as { c: Term[] }).c[1]!.k === 'add') {
      for (const y of ((x as { c: Term[] }).c[1] as { c: Term[] }).c)
        c.push(
          isNumT(y)
            ? num(Q.neg(y.v))
            : isNegation(y)
              ? (y as { c: Term[] }).c[1]!
              : { k: 'mul', c: [num(-1), y] },
        );
    } else if (x.k === 'add') c.push(...x.c);
    else c.push(x);
  }
  return { k: 'add', c };
}

/** Sommes dans les sommes, produits dans les produits : à plat (nombres gardés tels quels). */
function flatten(t: Term): Term {
  switch (t.k) {
    case 'add':
    case 'mul': {
      const c: Term[] = [];
      for (const x of t.c.map(flatten)) {
        if (x.k === t.k && !isNegation(x)) c.push(...(x as { c: Term[] }).c);
        else c.push(x);
      }
      return { k: t.k, c };
    }
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: flatten(t.a), b: flatten(t.b) };
    case 'fn':
      return { ...t, a: flatten(t.a) };
    default:
      return t;
  }
}

/** « x − y » → « 10 − y − y » : une grandeur remplacée par son expression établie (x = 10 − y). */
function substitutionMove(ks: KForState, a: Term, b: Term): Ev | null {
  const names = [...symbolsOf(a)].filter((n) => !symbolsOf(b).has(n));
  if (!names.length || names.length > 2) return null;
  const options = names.map((n) => expressionsOf(ks, n).map((e) => [n, e] as const));
  if (options.some((o) => !o.length)) return null;
  const target = shapeKey(flatten(b));
  const tryAll = (i: number, map: Record<string, Term>): boolean => {
    if (i === options.length) return shapeKey(flatten(substitute(a, map))) === target;
    return options[i]!.some(([n, e]) => tryAll(i + 1, { ...map, [n]: e }));
  };
  return tryAll(0, {}) ? { ...NONE, replace: names, expr: true } : null;
}

/** Les expressions établies d'une grandeur : « a = 2 − b » (lignes de confiance). */
function expressionsOf(ks: KForState, n: string): Term[] {
  const out: Term[] = [];
  for (const f of facts(ks)) {
    if (f.l.k === 'sym' && f.l.n === n && !isConstTerm(f.r) && !symbolsOf(f.r).has(n))
      out.push(f.r);
    if (f.r.k === 'sym' && f.r.n === n && !isConstTerm(f.l) && !symbolsOf(f.l).has(n))
      out.push(f.l);
  }
  return out;
}

/** « k₁X + k₂X » → « (k₁ + k₂)X » : des termes semblables réunis (même facteur X). */
function likeTerms(a: Term, b: Term): boolean {
  if (a.k !== 'add') return false;
  if (a.c.length > 2) {
    // « 40 − 2l + 4l » → « 2l + 40 » : deux termes réunis, les autres gardés
    if (b.k !== 'add' || b.c.length !== a.c.length - 1) return false;
    for (let i = 0; i < a.c.length; i++)
      for (let j = i + 1; j < a.c.length; j++) {
        const rest = a.c.filter((_, k) => k !== i && k !== j).map(shapeKey);
        for (const bt of b.c) {
          const others = b.c.filter((x) => x !== bt).map(shapeKey);
          if (others.length !== rest.length) continue;
          if ([...others].sort().join('|') !== [...rest].sort().join('|')) continue;
          if (likeTerms({ k: 'add', c: [a.c[i]!, a.c[j]!] }, bt)) return true;
        }
      }
    return false;
  }
  if (a.c.length !== 2) return false;
  const split = (t0: Term): { k: Q.Rat; x: string } | null => {
    // « − 2x » (lu −(2x)) : le coefficient −2
    const t = foldNeg(t0);
    if (t.k === 'mul' && t.c.length === 2) {
      const k = ratOf(t.c[0]!);
      if (k && ratOf(t.c[1]!) === null) return { k, x: shapeKey(t.c[1]!) };
      const k2 = ratOf(t.c[1]!);
      if (k2 && ratOf(t.c[0]!) === null) return { k: k2, x: shapeKey(t.c[0]!) };
    }
    if (ratOf(t) === null && t.k !== 'add') return { k: Q.ONE, x: shapeKey(t) };
    return null;
  };
  const p = split(a.c[0]!);
  const q = split(a.c[1]!);
  const r = split(b);
  if (!p || !q || !r || p.x !== q.x || q.x !== r.x) return false;
  return Q.eq(Q.add(p.k, q.k), r.k);
}

/** « k × (u + v) » → « k × u + k × v » (k devant ou derrière, opérandes dans tout ordre). */
function expandMove(ks: KForState, a: Term, b: Term, depth: number): Ev | null {
  if (a.k !== 'mul' || b.k !== 'add' || a.c.length !== 2) return null;
  const si = a.c.findIndex((c) => c.k === 'add');
  if (si < 0) return null;
  const sum = a.c[si] as { k: 'add'; c: Term[] };
  const k = a.c[1 - si]!;
  if (sum.c.length !== b.c.length) return null;
  const want = sum.c.map((u) => ({ k: 'mul', c: [k, u] }) as Term);
  // chaque terme développé est « k × u » (ou « −k × u » lu avec son signe)
  const m = matchUnordered(ks, want, b.c, depth);
  if (!m || m.deep) return null;
  return merge(m, { ...NONE, expand: 1 });
}

/** Deux expressions égales pour toutes les valeurs des grandeurs ? */
function sameExpr(a: Term, b: Term): boolean {
  return compareTerms(a, b) === 'equal';
}

function sameShapeMove(ks: KForState, a: Term, b: Term, depth: number): Ev | null {
  if (a.k !== b.k) return null;
  switch (a.k) {
    case 'div':
    case 'pow':
    case 'binom': {
      const bb = b as typeof a;
      const x = memberMove(ks, a.a, bb.a, depth + 1);
      const y = x && memberMove(ks, a.b, bb.b, depth + 1);
      return x && y ? merge(x, y) : null;
    }
    case 'fn': {
      const bb = b as typeof a;
      if (bb.f !== a.f) return null;
      return memberMove(ks, a.a, bb.a, depth + 1);
    }
    case 'add':
    case 'mul':
      return naryMove(ks, a, b as typeof a, depth);
    default:
      return null;
  }
}

/** Sommes et produits : opérandes appariés dans n'importe quel ordre, et des nombres réunis. */
function naryMove(
  ks: KForState,
  a: { k: 'add' | 'mul'; c: Term[] },
  b: { k: 'add' | 'mul'; c: Term[] },
  depth: number,
): Ev | null {
  if (a.c.length === b.c.length) {
    // dans l'ordre d'abord
    let ev: Ev | null = NONE;
    for (let i = 0; i < a.c.length && ev; i++) {
      const m = memberMove(ks, a.c[i]!, b.c[i]!, depth + 1);
      ev = m ? merge(ev, m) : null;
    }
    if (ev) return ev;
    const p = matchUnordered(ks, a.c, b.c, depth);
    return p ? { ...p, reorder: true } : null;
  }
  // une grandeur remplacée par une somme, sans parenthèses : « F(b) − F(a) » →
  // « π/8 − 1/4 − 0 » (les termes de `b` regroupés dans l'ordre, un groupe par opérande)
  if (a.k === 'add' && b.c.length > a.c.length && b.c.length <= 8) {
    const n = a.c.length;
    const m = b.c.length;
    let best: Ev | null = null;
    const cuts = (start: number, left: number, acc: number[]): void => {
      if (left === 1) {
        const sizes = [...acc, m - start];
        let ev: Ev | null = NONE;
        let pos = 0;
        sizes.forEach((sz, i) => {
          if (!ev) return;
          const part: Term = sz === 1 ? b.c[pos]! : { k: 'add', c: b.c.slice(pos, pos + sz) };
          pos += sz;
          const mm = memberMove(ks, a.c[i]!, part, depth + 1);
          ev = mm ? merge(ev, mm) : null;
        });
        // seulement une grandeur remplacée par sa valeur (sinon : enlever des parenthèses)
        if (ev && ev.replace.length && (!best || cost(ev) < cost(best))) best = ev;
        return;
      }
      for (let sz = 1; start + sz <= m - (left - 1); sz++) cuts(start + sz, left - 1, [...acc, sz]);
    };
    cuts(0, n, []);
    if (best) return best;
  }
  // des nombres réunis : « 4 × 4 × 4 » → « 16 × 4 », « 3 + x + 5 » → « x + 8 »
  if (b.c.length < a.c.length) {
    const isR = (t: Term) => ratOf(t) !== null;
    const aNum = a.c.filter(isR).map((t) => num(ratOf(t)!) as { k: 'num'; v: Q.Rat });
    const bNum = b.c.filter(isR).map((t) => num(ratOf(t)!) as { k: 'num'; v: Q.Rat });
    const aRest = a.c.filter((c) => !isR(c));
    const bRest = b.c.filter((c) => !isR(c));
    const done = aNum.length - bNum.length;
    if (done >= 1 && aRest.length === bRest.length) {
      const rest = aRest.length ? matchUnordered(ks, aRest, bRest, depth) : NONE;
      if (rest && sameCombined(a.k, aNum, bNum))
        return merge(rest, done === 1 ? { ...NONE, leaf: 1 } : { ...NONE, deep: done });
    }
  }
  return null;
}

/** Les nombres `after` sont-ils ceux de `before`, dont certains réunis (somme ou produit) ? */
function sameCombined(
  k: 'add' | 'mul',
  before: { k: 'num'; v: Q.Rat }[],
  after: { k: 'num'; v: Q.Rat }[],
): boolean {
  const combine = (xs: Q.Rat[]): Q.Rat =>
    xs.reduce((s, x) => (k === 'add' ? Q.add(s, x) : Q.mul(s, x)), k === 'add' ? Q.ZERO : Q.ONE);
  if (!Q.eq(combine(before.map((n) => n.v)), combine(after.map((n) => n.v)))) return false;
  // chaque nombre gardé est encore là, au moins un nouveau
  const pool = before.map((n) => Q.ratToText(n.v));
  let fresh = 0;
  for (const n of after) {
    const i = pool.indexOf(Q.ratToText(n.v));
    if (i >= 0) pool.splice(i, 1);
    else fresh++;
  }
  return fresh === 1 || (fresh === 0 && pool.length > 0);
}

/** Apparie deux listes d'opérandes sans tenir compte de l'ordre (le moins coûteux). */
function matchUnordered(ks: KForState, as: Term[], bs: Term[], depth: number): Ev | null {
  if (as.length !== bs.length || as.length > 7) return null;
  const used = new Array<boolean>(bs.length).fill(false);
  let total: Ev = NONE;
  // d'abord les opérandes identiques
  const left: Term[] = [];
  for (const x of as) {
    const k = shapeKey(x);
    const j = bs.findIndex((y, i) => !used[i] && shapeKey(y) === k);
    if (j >= 0) used[j] = true;
    else left.push(x);
  }
  for (const x of left) {
    let best: { j: number; ev: Ev } | null = null;
    for (let j = 0; j < bs.length; j++) {
      if (used[j]) continue;
      const m = memberMove(ks, x, bs[j]!, depth + 1);
      if (m && (!best || cost(m) < cost(best.ev))) best = { j, ev: m };
    }
    if (!best) return null;
    used[best.j] = true;
    total = merge(total, best.ev);
  }
  return total;
}

/* ------------------------------ un pas sur une relation ------------------------------ */

type MoveKind = 'same' | 'reorder' | 'replace' | 'compute' | 'expand' | 'jump';

interface RelMove {
  kind: MoveKind;
  swapped: boolean;
  names: string[];
  leaf: number;
}

function classify(ks: KForState, e: Ev, known: Set<string>): MoveKind {
  if (e.deep > 0) return 'jump';
  if (e.replace.length && (e.leaf || e.expand)) return 'jump';
  if (e.expand && e.leaf) return 'jump';
  if (e.replace.length) return e.expr || e.replace.every((n) => known.has(n)) ? 'replace' : 'jump';
  if (e.expand) return 'expand';
  if (e.leaf) return 'compute';
  return e.reorder ? 'reorder' : 'same';
}

/** Le passage de la relation `f` à la relation `t` par un pas sur ses membres. */
function relMove(ks: KForState, f: Rel2, t: Rel2, known: Set<string>): RelMove | null {
  let best: RelMove | null = null;
  const rank: Record<MoveKind, number> = {
    same: 0,
    reorder: 1,
    replace: 2,
    compute: 2,
    expand: 2,
    jump: 9,
  };
  for (const swapped of [false, true]) {
    const l = memberMove(ks, f.l, swapped ? t.r : t.l);
    const r = l && memberMove(ks, f.r, swapped ? t.l : t.r);
    if (!l || !r) continue;
    const e = merge(l, r);
    const kind = classify(ks, e, known);
    const mv: RelMove = { kind, swapped, names: [...new Set(e.replace)], leaf: e.leaf };
    if (!best || rank[kind] < rank[best.kind]) best = mv;
  }
  return best;
}

/** Inconnues de la relation (grandeurs dont la valeur n'est pas encore établie). */
function unknownsIn(r: Rel2, known: Set<string>): string[] {
  return [...new Set([...symbolsOf(r.l), ...symbolsOf(r.r)])].filter((n) => !known.has(n));
}

/**
 * Ce qu'il reste à faire pour isoler les inconnues (plus petit = mieux) : inconnue des
 * deux côtés, produits à développer, divisions, opérations autour de l'inconnue, puis
 * ailleurs, occurrences ; l'inconnue seule à droite coûte un échange de membres.
 */
function isolation(r: Rel2, known: Set<string>): number {
  const all = unknownsIn(r, known);
  if (all.length <= 1) return isolationFor(r, all);
  // plusieurs inconnues (système) : isoler l'une d'elles est déjà un progrès
  return Math.min(...all.map((x) => isolationFor(r, [x])));
}
function isolationFor(r: Rel2, u: string[]): number {
  if (!u.length) return 0;
  const has = (t: Term) => u.some((n) => symbolsOf(t).has(n));
  const lone = (t: Term) => t.k === 'sym' && u.includes(t.n);
  if (lone(r.l) && !has(r.r)) return 0;
  if (lone(r.r) && !has(r.l)) return 1;
  const count = (t: Term, f: (x: Term) => boolean): number => {
    let n = f(t) ? 1 : 0;
    if (t.k === 'add' || t.k === 'mul') n += t.c.reduce((s, c) => s + count(c, f), 0);
    else if (t.k === 'div' || t.k === 'pow' || t.k === 'binom') n += count(t.a, f) + count(t.b, f);
    else if (t.k === 'fn') n += count(t.a, f);
    return n;
  };
  const unexpanded = (t: Term) =>
    count(
      t,
      (x) =>
        (x.k === 'mul' && x.c.some((c) => c.k === 'add' && has(c))) ||
        (x.k === 'pow' && x.a.k === 'add' && has(x.a)),
    );
  const divs = (t: Term) => count(t, (x) => x.k === 'div' && has(x));
  const occ = (t: Term) => count(t, (x) => x.k === 'sym' && u.includes(x.n));
  const both = has(r.l) && has(r.r) ? 100 : 0;
  const side = (t: Term) => (has(t) ? 3 * opsOf(t) : opsOf(t));
  return (
    both +
    50 * (unexpanded(r.l) + unexpanded(r.r)) +
    10 * (divs(r.l) + divs(r.r)) +
    side(r.l) +
    side(r.r) +
    occ(r.l) +
    occ(r.r) +
    (has(r.l) ? 0 : 1)
  );
}

/* ------------------------------ juge ------------------------------ */

const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const quoted = (t: Term) => `« ${formulaText(t)} »`;

function knownAtoms(ks: KForState): string[][] {
  if (!ks.known) {
    const texts = [...ks.path, ks.model.kernel!.answer];
    ks.known = texts.flatMap((t) => {
      try {
        return [kforLineAtoms(t)];
      } catch {
        return [];
      }
    });
  }
  return ks.known;
}

export function kforJudge(ks: KForState, chips: Chip[], opts?: { verify?: boolean }): KForVerdict {
  const norm = normalizeChips(chips);
  const A = norm.atoms;
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const prefix = knownAtoms(ks).some((k) => k.length > A.length && A.every((a, i) => k[i] === a));
  // la syntaxe se lit sans la valeur approchée (« … = √70 ≈ 8,37 »)
  const ax = A.lastIndexOf('≈');
  const head = ax >= 0 && A.slice(0, ax).includes('=') ? A.slice(0, ax) : A;
  const syn = checkSyntax(head);
  if (!syn.ok) {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: syn.msg,
      badChip: norm.src[syn.index] !== undefined ? norm.src[syn.index]! : chips.length - 1,
    };
  }
  const tailOk = ax < 0 || (ax < A.length - 1 && checkSyntax(A.slice(ax + 1)).ok);
  if (!syn.complete || (!syn.hasEq && ax < 0) || !tailOk)
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  let F: FLine;
  try {
    F = readAtoms(A);
  } catch {
    if (prefix) return { kind: 'building', atoms: A, complete: false };
    return {
      kind: 'unjustified',
      atoms: A,
      title: 'Une relation par ligne',
      message:
        'Écris une égalité (avec « = »), éventuellement suivie d’une valeur approchée (« ≈ »), ou une comparaison.',
    };
  }
  const v = evaluate(ks, F, A);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready' };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return v;
}

function evaluate(ks: KForState, F: FLine, A: string[]): KForVerdict {
  const E = F.line;
  // lettres inconnues de la question
  const letters = new Set<string>();
  F.members.forEach((m) => symbolsOf(m).forEach((n) => letters.add(n)));
  if (F.approx) symbolsOf(F.approx).forEach((n) => letters.add(n));
  const stray = [...letters].filter((n) => !(n in ks.Wf));
  if (stray.length)
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Lettre inconnue',
      message: `« ${stray[0]} » n’est pas une grandeur de cette question : utilise celles de l’énoncé.`,
    };
  if (F.cmp) return comparisonVerdict(ks, F, A);
  const known = knownNames(ks);

  // 1. chaque maillon est-il vrai ?
  for (let i = 0; i + 1 < F.members.length; i++) {
    const a = F.members[i]!;
    const b = F.members[i + 1]!;
    const same = sameValue(ks, a, b);
    if (same === true) continue;
    if (same === null)
      return {
        kind: 'useless',
        atoms: A,
        line: E,
        unknown: true,
        title: 'Je ne sais pas encore vérifier ce passage',
        message:
          'Ta ligne est peut-être juste, mais je ne sais pas la vérifier. Garde-la sans HORA, ou écris le passage autrement.',
        kfor: { rels: [], trusted: false },
      };
    return falseVerdict(ks, F, i, A);
  }

  // 2. la valeur approchée finale
  if (F.approx) return approxVerdict(ks, F, A, known);

  // 3. piège reconnu même vrai ? (non : un piège est faux dans le monde) — justification
  const rels: Rel2[] = [];
  const head = F.members[0]!;
  for (let j = 1; j < F.members.length; j++) rels.push({ l: head, r: F.members[j]! });
  for (let j = 2; j < F.members.length; j++) rels.push({ l: F.members[j - 1]!, r: F.members[j]! });

  // déjà écrit, énoncé
  const all = facts(ks);
  const first: Rel2 = { l: F.members[0]!, r: F.members[1]! };
  const keys = relKeys(first);
  if (F.members.length === 2 && ks.given.some((g) => keys.includes(g.key)))
    return {
      kind: 'given',
      atoms: A,
      line: E,
      title: 'C’est l’énoncé',
      message: 'Cette égalité est déjà en tête de ta feuille : sers-t’en pour la suite.',
    };
  const isOld = (r: Rel2) => {
    const k = relKeys(r);
    return all.some((f) => k.includes(f.key));
  };
  if (rels.every(isOld))
    return {
      kind: 'dup',
      atoms: A,
      line: E,
      title: 'Déjà écrit',
      message: 'Tu as déjà cette ligne sur ta feuille.',
    };

  // 4. justifier chaque maillon
  const steps: KForRule[] = [];
  let tool: number | undefined;
  const j1 = justifyFirst(ks, first, all, known);
  if (j1.kind === 'step') {
    steps.push(j1.rule);
    tool = j1.tool;
  } else if (j1.kind === 'regress')
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Juste, mais ça ne t’avance pas',
      message:
        'Ta ligne est juste, mais elle ne rapproche pas de la réponse : isole la grandeur cherchée.',
      kfor: { rels, trusted: true },
    };
  let jumped = j1.kind === 'jump';
  let prev = first;
  for (let j = 2; j < F.members.length && !jumped; j++) {
    const next: Rel2 = { l: head, r: F.members[j]! };
    const mv = relMove(ks, prev, next, known);
    if (!mv || mv.kind === 'jump') jumped = true;
    else if (mv.kind === 'replace') steps.push(replaceRule(mv.names));
    else if (mv.kind === 'compute') steps.push(computeRule());
    else if (mv.kind === 'expand') steps.push(expandRule());
    // un simple réarrangement (autre ordre) ne rapporte rien
    prev = next;
  }

  // plusieurs grandeurs cherchées (« a », « b », « r ») : la réponse est complète quand
  // toutes sont établies, avec cette ligne
  const reaches = reachesGoal(ks, rels, known);
  const final = reaches && allGoalsWith(ks, rels);
  const sideCalc = rels.every((r) => unknownsIn(r, known).length === 0) && !reaches;
  if (jumped)
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      jump: true,
      final,
      title: final ? 'Juste !' : 'Juste, mais tu sautes des étapes',
      message: final
        ? 'C’est la bonne valeur. Détaille ton calcul, une étape par ligne, pour gagner des HORA. Tu peux garder cette ligne sans HORA.'
        : 'Ta ligne est juste. Détaille-la, une étape par ligne, pour gagner des HORA. Tu peux la garder sans HORA.',
      kfor: { rels, trusted: true },
    };
  if (sideCalc)
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Calcul juste',
      message:
        'Ce calcul est juste. Garde-le s’il t’aide : il ne rapporte pas de HORA, la ligne utile parle de la grandeur cherchée.',
      kfor: { rels, trusted: true },
    };
  if (!steps.length)
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      title: 'Même chose, écrite autrement',
      message:
        'Ta ligne est juste, mais elle redit ce que tu as déjà écrit, dans un autre ordre. Avance d’une étape : remplace ou calcule.',
      kfor: { rels, trusted: true },
    };
  const rule = mergeRules(steps);
  return {
    kind: 'step',
    atoms: A,
    line: E,
    hora: rule.hora,
    final,
    rule,
    kfor: { rels, trusted: true, ...(tool !== undefined ? { tool } : {}) },
  };
}

function mergeRules(steps: KForRule[]): KForRule {
  if (steps.length === 1) return steps[0]!;
  const labels = steps.map((s) => s.label).filter((l, i, a) => a.indexOf(l) === i);
  return {
    id: steps[0]!.id,
    card: steps[0]!.card,
    label: labels.join(' → '),
    hora: steps.reduce((s, x) => s + x.hora, 0),
    why: steps.map((s) => s.why).join(' '),
  };
}

function replaceRule(names: string[]): KForRule {
  const list =
    names.length <= 1
      ? (names[0] ?? 'la grandeur')
      : names.slice(0, -1).join(', ') + ' et ' + names[names.length - 1];
  return {
    id: FORMULA_STEP_IDS.replace,
    card: 'Calcul',
    label: 'Remplacer',
    hora: 2,
    why: `On remplace ${list} par ${names.length > 1 ? 'leurs valeurs' : 'sa valeur'}.`,
  };
}
function expandRule(): KForRule {
  return {
    id: 'kernel.expand@1',
    card: 'Calcul littéral',
    label: 'Développer',
    hora: 1,
    why: 'On distribue : k × (a + b) = k × a + k × b.',
  };
}
function computeRule(): KForRule {
  return {
    id: FORMULA_STEP_IDS.compute,
    card: 'Calcul',
    label: 'Calculer',
    hora: 1,
    why: 'On fait un calcul (un seul par endroit) : la valeur ne change pas, elle s’écrit plus simplement.',
  };
}

type FirstJustification =
  | { kind: 'step'; rule: KForRule; tool?: number }
  | { kind: 'jump' }
  | { kind: 'regress' };

/** Le premier maillon : une formule de la carte, ou un pas depuis une ligne de confiance. */
function justifyFirst(ks: KForState, t: Rel2, all: Fact[], known: Set<string>): FirstJustification {
  // un pas sur les membres d'une ligne de confiance
  let best: { mv: RelMove; f: Fact } | null = null;
  const rank: Record<MoveKind, number> = {
    same: 9,
    reorder: 8,
    replace: 1,
    compute: 1,
    expand: 2,
    jump: 9,
  };
  for (const f of all) {
    const mv = relMove(ks, f, t, known);
    if (!mv) continue;
    if (mv.kind === 'same' && mv.swapped) {
      // échanger les membres : seulement pour mettre l'inconnue à gauche
      if (isolation(t, known) < isolation(f, known))
        return {
          kind: 'step',
          rule: {
            id: FORMULA_STEP_IDS.swap,
            card: 'Calcul',
            label: 'Échanger les membres',
            hora: 1,
            why: 'On lit la même égalité dans l’autre sens : la grandeur cherchée passe à gauche.',
          },
        };
      continue;
    }
    if (!best || rank[mv.kind] < rank[best.mv.kind]) best = { mv, f };
    if (rank[mv.kind] === 1) break;
  }
  if (best?.mv.kind === 'replace') return { kind: 'step', rule: replaceRule(best.mv.names) };
  if (best?.mv.kind === 'compute') return { kind: 'step', rule: computeRule() };
  if (best?.mv.kind === 'expand') return { kind: 'step', rule: expandRule() };
  // une formule de la carte pas encore écrite : telle quelle, réarrangée, ou déjà remplie
  const tf = toolOf(ks, t, known);
  if (tf) return tf;
  // une règle d'équation : l'inconnue est prise dans une relation
  const u = unknownsIn(t, known);
  if (u.length) {
    const r = algebraStep(ks, t, all, known);
    if (r) return r;
  }
  return { kind: 'jump' };
}

/** Une formule de la carte (réarrangée, ou déjà remplie avec des valeurs connues) ? */
function toolOf(ks: KForState, t: Rel2, known: Set<string>): FirstJustification | null {
  const tRf = diffRf(t);
  for (let i = 0; i < ks.tools.length; i++) {
    const tool = ks.tools[i]!;
    if (ks.lines.some((l) => l.tool === i)) continue;
    const step = (extra: string): FirstJustification => ({
      kind: 'step',
      tool: i,
      rule: {
        id: FORMULA_STEP_IDS.tool,
        card: tool.card,
        label: tool.label,
        hora: tool.hora,
        why: tool.why + extra,
      },
    });
    const keys = relKeys(t);
    if (keys.includes(relKey(tool.rel))) return step('');
    // réarrangée : mêmes grandeurs, même relation à un facteur près
    const names = new Set([...symbolsOf(t.l), ...symbolsOf(t.r)]);
    if (
      names.size === tool.names.size &&
      [...names].every((n) => tool.names.has(n)) &&
      proportional(tRf, tool.rf)
    )
      return step('');
    // déjà remplie : des grandeurs connues remplacées par leur valeur
    const mv = relMove(ks, tool.rel, t, known);
    if (mv && (mv.kind === 'replace' || (mv.kind === 'same' && mv.swapped)))
      return step(mv.kind === 'replace' ? ' On remplace aussitôt par les valeurs connues.' : '');
  }
  return null;
}

/** Une règle d'équation du noyau, depuis une relation de confiance qui a une inconnue. */
function algebraStep(
  ks: KForState,
  t: Rel2,
  all: Fact[],
  known: Set<string>,
): FirstJustification | null {
  const to: Statement = { k: 'rel', rel: '=', l: t.l, r: t.r };
  let tried = 0;
  for (const f of all) {
    if (!unknownsIn(f, known).length) continue;
    if (++tried > 8) break;
    const from: Statement = { k: 'rel', rel: '=', l: f.l, r: f.r };
    let c: StepCheck;
    try {
      c = checkAlgebraStatements(from, to, {
        rules: kernelRules(),
        hypotheses: ks.hyps,
        branches: false,
      });
    } catch {
      continue;
    }
    if (c.status !== 'valid' || c.proof !== 'rule' || !c.ruleId) continue;
    // la règle et son calcul, rien de plus (pas de termes semblables réunis en passant)
    if (workOf(t) < workOf(f)) return { kind: 'jump' };
    if (isolation(t, known) >= isolation(f, known)) return { kind: 'regress' };
    const info = kernelRuleStep(c, to, from);
    return {
      kind: 'step',
      rule: { id: info.id, card: info.card, label: info.label, hora: info.hora, why: info.why },
    };
  }
  return null;
}

/** Calculs en attente d'une relation : opérations sur des nombres, termes semblables. */
function workOf(r: Rel2): number {
  const count = (t: Term): number => {
    let n = isPending(t) ? Math.max(1, opsOf(t)) : 0;
    if (n) return n;
    if (t.k === 'add') {
      // termes semblables : chaque terme qui partage son facteur avec un précédent
      const seen = new Set<string>();
      for (const c of t.c) {
        const f = likeFactor(c);
        if (f !== null) {
          if (seen.has(f)) n++;
          seen.add(f);
        }
      }
    }
    if (t.k === 'add' || t.k === 'mul') return n + t.c.reduce((s, c) => s + count(c), 0);
    if (t.k === 'div' || t.k === 'pow' || t.k === 'binom') return n + count(t.a) + count(t.b);
    if (t.k === 'fn') return n + count(t.a);
    return n;
  };
  return count(r.l) + count(r.r);
}
/** Le facteur non numérique d'un terme (« 2x » → x, « −3√2 » → √2), null pour un nombre. */
function likeFactor(t: Term): string | null {
  if (isNegation(t)) return likeFactor((t as { c: Term[] }).c[1]!);
  if (ratOf(t) !== null) return null;
  if (t.k === 'mul') {
    const rest = t.c.filter((c) => ratOf(c) === null);
    if (rest.length === 1) return shapeKey(rest[0]!);
  }
  return isConstTerm(t) ? null : shapeKey(t);
}

/** La ligne établit-elle la valeur d'une grandeur cherchée, sous sa forme finale ? */
function reachesGoal(ks: KForState, rels: Rel2[], known: Set<string>): boolean {
  void known;
  if (roundingGoal(ks) && !ks.model.rounding) return false;
  return rels.some((r) => goalOfRel(ks, r) !== null) && !roundingPending(ks, rels);
}

/** Toutes les grandeurs cherchées sont-elles établies, avec les relations `rels` ? */
function allGoalsWith(ks: KForState, rels: Rel2[]): boolean {
  if (ks.goals.length < 2) return true;
  const all = [...facts(ks), ...rels];
  return ks.goals.every((g) => all.some((f) => goalOfRel(ks, f) === g));
}

/** « G = valeur finale » : la grandeur cherchée G. */
function goalOfRel(ks: KForState, r: Rel2): string | null {
  for (const [g, v] of [
    [r.l, r.r],
    [r.r, r.l],
  ] as const) {
    if (g.k !== 'sym' || !ks.goals.includes(g.n)) continue;
    if (!isConstTerm(v) || !isFinalForm(ks, g.n, v)) continue;
    return g.n;
  }
  return null;
}

/** La valeur est-elle écrite sous sa forme finale (rien à calculer de plus) ? */
function isFinalForm(ks: KForState, g: string, v: Term): boolean {
  if (isNumT(v)) return true;
  if (opsOf(v) === 0) return true;
  // la forme de la réponse du modèle (« 5π ÷ 6 », « (ln(3) + 1) ÷ 2 »)
  const ans = answerValueOf(ks, g);
  if (ans && shapeKey(ans) === shapeKey(v)) return true;
  // une forme exacte sans calcul en attente
  if (!isValueForm(v)) return false;
  if (ans && opsOf(v) > Math.max(opsOf(ans), canonicalOps(v)) + 1) return false;
  return true;
}

/** « 5 ÷ 6 » : une fraction d'entiers irréductible est une valeur écrite, pas un calcul. */
function isIrreducibleFraction(t: Term): boolean {
  if (t.k !== 'div' || !isNumT(t.a) || !isNumT(t.b)) return false;
  const a = t.a.v;
  const b = t.b.v;
  if (!Q.isInteger(a) || !Q.isInteger(b) || b.n <= 1n) return false;
  const g = (x: bigint, y: bigint): bigint => (y === 0n ? (x < 0n ? -x : x) : g(y, x % y));
  return g(a.n, b.n) === 1n;
}

/** Un calcul en attente : une opération sans grandeur dont le résultat est un nombre rationnel. */
function isPending(t: Term): boolean {
  if (t.k === 'num' || t.k === 'sym' || isNegation(t) || isIrreducibleFraction(t)) return false;
  if (t.k === 'fn' && t.f === 'deg') return false;
  if (!isConstTerm(t)) return false;
  const n = normalForm(t);
  return n.ok && !!rfRat(n.rf);
}

/** Reste-t-il un calcul à faire quelque part dans le terme ? */
function hasLeafOp(t: Term): boolean {
  if (isPending(t)) return true;
  switch (t.k) {
    case 'num':
    case 'sym':
      return false;
    case 'add':
    case 'mul':
      return t.c.some(hasLeafOp);
    case 'div':
    case 'pow':
    case 'binom':
      return hasLeafOp(t.a) || hasLeafOp(t.b);
    case 'fn':
      return hasLeafOp(t.a);
  }
}

function answerValueOf(ks: KForState, g: string): Term | null {
  const a = ks.answer;
  if (!a || a.k !== 'rel') return null;
  if (a.l.k === 'sym' && a.l.n === g) return a.r;
  if (a.r.k === 'sym' && a.r.n === g) return a.l;
  return null;
}

/* ------------------------------ arrondis ------------------------------ */

/** La grandeur à arrondir (`rounding` du modèle), null sinon. */
function roundingGoal(ks: KForState): string | null {
  const r = ks.model.rounding;
  return r ? (r.symbol ?? ks.goals[0]!) : null;
}
/** Une réponse à arrondir n'est finie qu'une fois arrondie. */
function roundingPending(ks: KForState, _rels: Rel2[]): boolean {
  void _rels;
  const g = roundingGoal(ks);
  if (!g) return false;
  return !ks.lines.some((l) => l.approx && l.approx.name === g);
}

function decimalsOf(t: Term): number | null {
  if (!isNumT(t)) return null;
  let d = t.v.d;
  let p = 0;
  while (d % 10n === 0n) {
    d /= 10n;
    p++;
  }
  if (d !== 1n) {
    // 1/4 = 0,25 : un dénominateur en 2 et 5 seulement
    let x = t.v.d;
    while (x % 2n === 0n || x % 5n === 0n) x = x % 2n === 0n ? x / 2n : x / 5n;
    if (x !== 1n) return null;
    for (let q = 0; q <= 12; q++) {
      const s = 10n ** BigInt(q);
      if ((s * t.v.n) % t.v.d === 0n) return q;
    }
    return null;
  }
  return p;
}

function roundingWords(p: number): string {
  return p === 0
    ? 'à l’unité'
    : p === 1
      ? 'au dixième'
      : p === 2
        ? 'au centième'
        : p === 3
          ? 'au millième'
          : `à ${p} chiffres après la virgule`;
}

function approxVerdict(ks: KForState, F: FLine, A: string[], known: Set<string>): KForVerdict {
  const E = F.line;
  const left = F.members[F.members.length - 1]!;
  const head = F.members[0]!;
  const v = F.approx!;
  const x = floatOf(ks, left);
  const vv = isConstTerm(v) ? evalFloat(v, {}) : NaN;
  const places = decimalsOf(v);
  if (!Number.isFinite(x) || !Number.isFinite(vv) || places === null)
    return {
      kind: 'unjustified',
      atoms: A,
      line: E,
      title: 'Une valeur approchée',
      message: 'Après « ≈ », écris un nombre décimal : la valeur approchée.',
    };
  const name = head.k === 'sym' ? head.n : null;
  const rounded = roundTo(x, places);
  const ok = Math.abs(rounded - vv) < 1e-9 * Math.max(1, Math.abs(vv));
  if (!ok) {
    const trunc = Math.trunc(x * 10 ** places) / 10 ** places;
    if (Math.abs(trunc - vv) < 1e-9 * Math.max(1, Math.abs(vv)))
      return {
        kind: 'false',
        atoms: A,
        line: E,
        title: 'Arrondir, ce n’est pas couper',
        message: `On regarde le chiffre suivant : s’il vaut 5 ou plus, on arrondit au-dessus. Arrondis ${roundingWords(places)}.`,
      };
    return {
      kind: 'false',
      atoms: A,
      line: E,
      title: 'Valeur approchée fausse',
      message: `${formulaText(left)} ne vaut pas environ ${formulaText(v)} : refais le calcul, puis arrondis ${roundingWords(places)}.`,
    };
  }
  // la chaîne avant « ≈ » doit être juste et justifiée comme une ligne ordinaire
  const rels: Rel2[] = [];
  for (let j = 1; j < F.members.length; j++) rels.push({ l: head, r: F.members[j]! });
  let chainStep: KForVerdict | null = null;
  if (
    F.members.length >= 2 &&
    !rels.every((r) => facts(ks).some((f) => relKeys(r).includes(f.key)))
  ) {
    chainStep = evaluate(ks, { ...F, approx: null }, A);
    if (chainStep.kind !== 'step' && chainStep.kind !== 'useless' && chainStep.kind !== 'dup')
      return chainStep;
  }
  const r = ks.model.rounding;
  const g = roundingGoal(ks);
  const isGoal = name !== null && name === g;
  const exactKnown =
    isGoal &&
    (F.members.length >= 2 ||
      facts(ks).some(
        (f) =>
          (f.l.k === 'sym' && f.l.n === name && isConstTerm(f.r)) ||
          (f.r.k === 'sym' && f.r.n === name && isConstTerm(f.l)),
      ));
  if (r && isGoal) {
    // « AB ≈ 5 » au dixième (5,0) : les zéros de la fin ne s'écrivent pas
    const exactPlaces =
      places < r.places && Math.abs(roundTo(x, r.places) - vv) < 1e-9 * Math.max(1, Math.abs(vv))
        ? r.places
        : places;
    if (exactPlaces !== r.places)
      return {
        kind: 'unjustified',
        atoms: A,
        line: E,
        title: places > r.places ? 'Garde moins de chiffres' : 'Pas assez précis',
        message: `La question demande une valeur arrondie ${roundingWords(r.places)}.`,
      };
    const rule: KForRule = {
      id: FORMULA_STEP_IDS.round,
      card: 'Calcul',
      label: 'Arrondir',
      hora: r.hora ?? 1,
      why: `On arrondit la valeur exacte ${roundingWords(r.places)}.`,
    };
    const commit: KForCommit = { rels, trusted: true, approx: { name, value: v } };
    if (!exactKnown)
      return {
        kind: 'useless',
        atoms: A,
        line: E,
        jump: true,
        final: true,
        title: 'Juste !',
        message:
          'C’est la bonne valeur approchée. Écris d’abord la valeur exacte, puis arrondis, pour gagner des HORA. Tu peux garder cette ligne sans HORA.',
        kfor: commit,
      };
    const prevRule = chainStep?.kind === 'step' ? chainStep.rule : null;
    const all = prevRule ? mergeRules([prevRule, rule]) : rule;
    return {
      kind: 'step',
      atoms: A,
      line: E,
      hora: all.hora,
      final: true,
      rule: all,
      kfor: commit,
    };
  }
  void known;
  return {
    kind: 'useless',
    atoms: A,
    line: E,
    title: 'Valeur approchée juste',
    message:
      'Ta valeur approchée est juste. Garde-la si elle t’aide à te repérer : la réponse attendue est la valeur exacte.',
    kfor: { rels: chainStep ? rels : [], trusted: true, approx: { name, value: v } },
  };
}

/* ------------------------------ erreurs ------------------------------ */

function falseVerdict(ks: KForState, F: FLine, i: number, A: string[]): KForVerdict {
  const E = F.line;
  const a = F.members[i]!;
  const b = F.members[i + 1]!;
  // un piège connu (tel quel, ou déjà rempli)
  const whole: Rel2 = { l: F.members[0]!, r: F.members[F.members.length - 1]! };
  for (const r of [whole, { l: a, r: b }]) {
    const tr = trapOf(ks, r);
    if (tr)
      return {
        kind: 'trap',
        atoms: A,
        line: E,
        title: 'Piège : ' + lower(tr.title),
        message: tr.why,
        trap: { line: tr.line, title: tr.title, why: tr.why },
        badChip: -1,
      };
  }
  // une valeur arrondie écrite avec « = »
  const dec = decimalsOf(b);
  if (dec !== null && isConstTerm(b)) {
    const x = floatOf(ks, a);
    const v = evalFloat(b, {});
    if (Number.isFinite(x) && Math.abs(roundTo(x, dec) - v) < 1e-9 * Math.max(1, Math.abs(v)))
      return {
        kind: 'unjustified',
        atoms: A,
        line: E,
        title: 'Écris « ≈ »',
        message: `${quoted(a)} ne vaut pas exactement ${formulaText(b)} : c’en est une valeur arrondie. Écris « ≈ » à la place de « = ».`,
      };
  }
  const numeric = isConstTerm(a) && isConstTerm(b);
  return {
    kind: 'false',
    atoms: A,
    line: E,
    title: numeric ? 'Calcul faux' : 'Ce n’est pas égal',
    message: numeric
      ? `${quoted(a)} ne fait pas ${formulaText(b)} : refais ce calcul.`
      : `${quoted(a)} et ${quoted(b)} n’ont pas la même valeur : vérifie la formule et les valeurs.`,
  };
}

function trapOf(ks: KForState, r: Rel2): FTrap | null {
  const keys = relKeys(r);
  const direct = ks.traps.find((t) => keys.includes(t.key));
  if (direct) return direct;
  const rf = diffRf(withGivenValues(ks, r));
  if (!rf) return null;
  // même erreur, à un facteur près, avec les mêmes grandeurs inconnues
  const names = (x: Rel2) =>
    [...new Set([...symbolsOf(x.l), ...symbolsOf(x.r)])].filter((n) => !ks.model.symbols[n]?.given);
  const mine = names(r).sort().join(',');
  return (
    ks.traps.find(
      (t) => proportional(rf, t.rf) && names(withGivenValues(ks, t.rel)).sort().join(',') === mine,
    ) ?? null
  );
}

/* ------------------------------ comparaisons ------------------------------ */

function comparisonVerdict(ks: KForState, F: FLine, A: string[]): KForVerdict {
  const E = F.line;
  const [a, b] = F.members as [Term, Term];
  const holds = cmpHolds(ks, F.cmp!, a, b);
  if (holds === null)
    return {
      kind: 'useless',
      atoms: A,
      line: E,
      unknown: true,
      title: 'Je ne sais pas encore vérifier ce passage',
      message:
        'Je ne sais pas trancher cette comparaison. Garde-la sans HORA, ou écris-la autrement.',
      kfor: { rels: [], trusted: false },
    };
  if (!holds)
    return {
      kind: 'false',
      atoms: A,
      line: E,
      title: 'Ce n’est pas vrai',
      message: `${quoted(a)} ${F.cmp} ${quoted(b)} est faux : compare les valeurs.`,
    };
  const key = `${shapeKey(a)}${F.cmp}${shapeKey(b)}`;
  const flip: Record<string, string> = { '>': '<', '<': '>', '≥': '≤', '≤': '≥' };
  const key2 = `${shapeKey(b)}${flip[F.cmp!]}${shapeKey(a)}`;
  const chk = ks.checks.find((c) => c.key === key || c.key === key2);
  if (chk) {
    if (ks.checked.has(chk.key))
      return {
        kind: 'dup',
        atoms: A,
        line: E,
        title: 'Déjà écrit',
        message: 'Tu as déjà cette comparaison sur ta feuille.',
      };
    // ses grandeurs doivent être connues
    const known = knownNames(ks);
    const miss = [...symbolsOf(a), ...symbolsOf(b)].filter((n) => !known.has(n));
    if (miss.length)
      return {
        kind: 'unjustified',
        atoms: A,
        line: E,
        title: `Calcule d’abord ${miss[0]}`,
        message: `Pour comparer, il faut d’abord la valeur de ${miss[0]}.`,
      };
    const rule: KForRule = {
      id: FORMULA_STEP_IDS.check,
      card: 'Calcul',
      label: chk.spec.label,
      hora: chk.spec.hora ?? 2,
      why: chk.spec.why,
    };
    return {
      kind: 'step',
      atoms: A,
      line: E,
      hora: rule.hora,
      rule,
      kfor: { rels: [], trusted: true, check: chk.key },
    };
  }
  return {
    kind: 'useless',
    atoms: A,
    line: E,
    title: 'Juste, mais à côté',
    message: 'Ta comparaison est juste, mais elle ne sert pas cette question. Tu peux la garder.',
    kfor: { rels: [], trusted: true },
  };
}

/* ------------------------------ feuille ------------------------------ */

/** Enregistre une ligne validée (pas) ou gardée (sans HORA). */
export function kforCommit(ks: KForState, v: KForVerdict): void {
  const c = v.kfor;
  if (!c) return;
  ks.lines.push({
    rels: c.rels,
    trusted: c.trusted,
    ...(c.approx ? { approx: c.approx } : {}),
    ...(c.tool !== undefined ? { tool: c.tool } : {}),
  });
  if (c.check) ks.checked.add(c.check);
}

/** Chaque grandeur cherchée a sa valeur finale (et arrondie si demandé). */
export function kforGoalReached(ks: KForState): boolean {
  const all = facts(ks);
  const done = ks.goals.every((g) => all.some((f) => goalOfRel(ks, f) === g));
  if (!done) {
    // une réponse arrondie gardée d'un bond (sans la valeur exacte)
    const g = roundingGoal(ks);
    if (!g || ks.goals.length !== 1) return false;
    return ks.lines.some((l) => l.approx?.name === g);
  }
  return !roundingPending(ks, []);
}

/** Valeurs établies des grandeurs cherchées (choix du QCM). */
export function kforStatedValues(ks: KForState): Record<string, number[]> {
  const out: Record<string, number[]> = {};
  const push = (g: string, v: number) => {
    if (!Number.isFinite(v)) return;
    const list = (out[g] ??= []);
    if (!list.some((x) => Math.abs(x - v) < 1e-9)) list.push(v);
  };
  for (const f of facts(ks)) {
    for (const [g, v] of [
      [f.l, f.r],
      [f.r, f.l],
    ] as const) {
      // une valeur sous sa forme finale (« P = 12 × 3 » n'est pas encore « 36 »)
      if (g.k === 'sym' && ks.goals.includes(g.n) && isConstTerm(v) && isFinalForm(ks, g.n, v))
        push(g.n, evalFloat(v, {}));
    }
  }
  for (const l of ks.lines)
    if (l.approx?.name && ks.goals.includes(l.approx.name))
      push(l.approx.name, evalFloat(l.approx.value, {}));
  return out;
}

/** Le texte est-il vrai dans le monde de la question ? */
export function kforHolds(ks: KForState, text: string): boolean {
  const F = readText(text);
  if (!F) return false;
  if (F.cmp) return cmpHolds(ks, F.cmp, F.members[0]!, F.members[1]!) === true;
  for (let i = 0; i + 1 < F.members.length; i++)
    if (sameValue(ks, F.members[i]!, F.members[i + 1]!) !== true) return false;
  if (F.approx) {
    const x = floatOf(ks, F.members[F.members.length - 1]!);
    const p = decimalsOf(F.approx);
    if (p === null) return false;
    return Math.abs(roundTo(x, p) - evalFloat(F.approx, {})) < 1e-9;
  }
  return true;
}

/** Le texte (un choix du QCM) donne-t-il la réponse ? */
export function kforIsAnswer(ks: KForState, text: string): boolean {
  const F = readText(text);
  if (!F || F.cmp) return false;
  const head = F.members[0]!;
  if (head.k === 'sym' && ks.goals.includes(head.n)) return kforHolds(ks, text);
  // la formule de la carte, en lettres (« AC² = AB² + BC² ») : juste ou non
  const lettersOnly = F.members.every((m) => symbolsOf(m).size > 0);
  return lettersOnly && F.members.length === 2 && kforHolds(ks, text);
}

/** Le texte est-il déjà établi sur la feuille ? */
export function kforEstablished(ks: KForState, text: string): boolean {
  const F = readText(text);
  if (!F || F.cmp || F.members.length < 2) return false;
  const all = facts(ks);
  const r: Rel2 = { l: F.members[0]!, r: F.members[F.members.length - 1]! };
  if (all.some((f) => relKeys(r).includes(f.key))) return true;
  // même valeur établie pour la même grandeur
  if (r.l.k === 'sym' && isConstTerm(r.r))
    return all.some(
      (f) =>
        f.l.k === 'sym' &&
        f.l.n === (r.l as { n: string }).n &&
        isConstTerm(f.r) &&
        sameValue(ks, f.r, r.r) === true,
    );
  return false;
}

/* ------------------------------ coup de pouce ------------------------------ */

export interface KForMove {
  line: Line;
  text: string;
  atoms: string[];
  label: string;
}

/** La prochaine ligne du corrigé, après la plus avancée déjà établie. */
export function kforNextMove(ks: KForState): KForMove | null {
  if (kforGoalReached(ks)) return null;
  let last = -1;
  ks.path.forEach((t, i) => {
    if (kforEstablishedLine(ks, t)) last = i;
  });
  for (let i = last + 1; i < ks.path.length; i++) {
    const t = ks.path[i]!;
    if (kforEstablishedLine(ks, t)) continue;
    const atoms = kforLineAtoms(t);
    let F: FLine;
    try {
      F = readAtoms(atoms);
    } catch {
      continue;
    }
    const v = evaluate(ks, F, atoms);
    return { line: F.line, text: t, atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

function kforEstablishedLine(ks: KForState, text: string): boolean {
  const F = readText(text);
  if (!F) return false;
  if (F.approx) {
    const head = F.members[0]!;
    const name = head.k === 'sym' ? head.n : null;
    return ks.lines.some((l) => l.approx && l.approx.name === name);
  }
  if (F.cmp) {
    const key = `${shapeKey(F.members[0]!)}${F.cmp}${shapeKey(F.members[1]!)}`;
    return ks.checked.has(key);
  }
  return kforEstablished(ks, text);
}

/** Le corrigé du modèle rejoué ligne à ligne (porte, palette, coup de pouce). */
export function kforSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KForVerdict }[];
  reached: boolean;
} {
  const ks = createKForState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KForVerdict }[] = [];
  for (const text of ks.path) {
    const atoms = kforLineAtoms(text);
    const v = kforJudge(
      ks,
      atoms.map((a) => ({ atoms: [a], text: a })),
      { verify: true },
    );
    lines.push({ line: v.line ?? { dis: [] }, text, atoms, v });
    if (v.kind === 'step' || v.kind === 'useless') kforCommit(ks, v);
  }
  return { lines, reached: kforGoalReached(ks) };
}

/* ------------------------------ palette ------------------------------ */

/**
 * Bouts d'une feuille à formules (D6) : les grandeurs, les nombres de l'énoncé (valeurs
 * données, égalités affichées, formules de la carte), puis les valeurs du CORRIGÉ (pas
 * celles des pièges ni des choix du QCM) ; « ≈ » toujours proposé.
 */
export function kforPaletteParts(model: ScratchModel): {
  names: string[];
  tools: string[];
  statementTexts: string[];
  givenValues: string[];
  pathTexts: string[];
  roundedValues: string[];
} {
  const names: string[] = [];
  const tools: string[] = [];
  Object.entries(model.symbols).forEach(([n, s]) => (s.tool ? tools : names).push(n));
  const { tools: cardTools } = pythagoreTools(model);
  const statementTexts = [
    ...(model.givens ?? []),
    ...(model.admitted ?? []),
    ...cardTools.map((t) => t.text),
    ...(model.checks ?? []).map((c) => c.line),
  ];
  const givenValues: string[] = [];
  Object.values(model.symbols).forEach((s) => {
    if (s.given && s.exact?.length) givenValues.push(s.exact[0]!);
    else if (s.given && s.value !== undefined) givenValues.push(formatNumber(s.value));
  });
  const pathTexts = [...(model.kernel?.path ?? []), model.kernel?.answer ?? ''].filter(Boolean);
  const roundedValues: string[] = [];
  if (model.rounding) {
    const g = model.rounding.symbol ?? model.goal[0]!;
    (model.symbols[g]?.values ?? []).forEach((v) =>
      roundedValues.push(formatNumber(roundTo(v, model.rounding!.places))),
    );
  }
  return { names, tools, statementTexts, givenValues, pathTexts, roundedValues };
}

/** Pour les tests : les atomes d'une ligne de texte, comme la feuille les lit. */
export function kforAtomsOf(text: string): string[] {
  return lineAtoms(parseText(text));
}
export type { Node };

/* ------------------------------ écriture ------------------------------ */

const P_ADD = 1;
const P_MUL = 2;
const P_UNARY = 3;
const P_POW = 4;
const P_ATOM = 5;

/** Un nombre rationnel écrit comme l'élève l'écrit : décimal (≤ 6 chiffres) ou fraction. */
function ratWritten(q: Q.Rat): { text: string; prec: number } {
  const neg = q.n < 0n;
  const n = neg ? -q.n : q.n;
  if (q.d === 1n) return { text: (neg ? '−' : '') + n.toString(), prec: neg ? P_UNARY : P_ATOM };
  let d = q.d;
  let places = 0;
  while (d % 2n === 0n || d % 5n === 0n) {
    d = d % 2n === 0n ? d / 2n : d / 5n;
    places++;
  }
  if (d === 1n) {
    for (let p = 1; p <= 6; p++) {
      const s = 10n ** BigInt(p);
      if ((s * n) % q.d === 0n) {
        const digits = ((n * s) / q.d).toString().padStart(p + 1, '0');
        return {
          text: (neg ? '−' : '') + digits.slice(0, -p) + ',' + digits.slice(-p),
          prec: neg ? P_UNARY : P_ATOM,
        };
      }
    }
  }
  void places;
  return { text: `${neg ? '−' : ''}${n}/${q.d}`, prec: neg ? P_UNARY : P_MUL };
}

function wrapW(x: { text: string; prec: number }, min: number): string {
  return x.prec < min ? `(${x.text})` : x.text;
}

function showW(t: Term): { text: string; prec: number } {
  switch (t.k) {
    case 'num':
      return ratWritten(t.v);
    case 'sym':
      return { text: t.n, prec: P_ATOM };
    case 'add': {
      let text = '';
      t.c.forEach((c, i) => {
        // « 4 − 4 × (−15) » plutôt que « 4 − (4 × (−15)) » : la soustraction d'un produit
        const inner = isNegation(c) ? (c as { c: Term[] }).c[1]! : null;
        const s =
          i > 0 && inner && (inner.k === 'mul' || inner.k === 'div') && !isNegation(inner)
            ? { text: '−' + showW(inner).text, prec: P_MUL }
            : showW(c);
        if (i === 0) text = s.text;
        else if (s.text.startsWith('−') && s.prec >= P_MUL) text += ` − ${s.text.slice(1)}`;
        else text += ` + ${s.prec <= P_ADD ? `(${s.text})` : s.text}`;
      });
      return { text, prec: P_ADD };
    }
    case 'mul': {
      if (isNegation(t)) {
        const s = showW(t.c[1]!);
        return { text: '−' + wrapW(s, P_POW), prec: P_UNARY };
      }
      let text = '';
      t.c.forEach((c, i) => {
        const s = showW(c);
        const part = i > 0 && s.text.startsWith('−') ? `(${s.text})` : wrapW(s, P_MUL + 0.5);
        if (i === 0) text = part;
        else {
          // « 18π », « 2√3 », « 5x » : un nombre suivi d'une lettre ou d'une racine
          const prev = t.c[i - 1]!;
          const glue =
            i === 1 &&
            isNumT(prev) &&
            Q.sign(prev.v) > 0 &&
            (c.k === 'sym' || (c.k === 'fn' && c.f === 'sqrt'));
          text += glue ? part : ` × ${part}`;
        }
      });
      return { text, prec: P_MUL };
    }
    case 'div':
      if (isIrreducibleFraction(t))
        return { text: `${showW(t.a).text}/${showW(t.b).text}`, prec: P_MUL };
      return { text: `${wrapW(showW(t.a), P_MUL)} ÷ ${wrapW(showW(t.b), P_POW)}`, prec: P_MUL };
    case 'pow': {
      const base = wrapW(showW(t.a), P_ATOM);
      if (isNumT(t.b) && Q.eq(t.b.v, Q.rat(2))) return { text: `${base}²`, prec: P_POW };
      return { text: `${base}^${wrapW(showW(t.b), P_ATOM)}`, prec: P_POW };
    }
    case 'binom':
      return {
        text: `${wrapW(showW(t.a), P_POW)} parmi ${wrapW(showW(t.b), P_POW)}`,
        prec: P_MUL,
      };
    case 'fn': {
      const a = showW(t.a);
      if (t.f === 'sqrt') return { text: `√${wrapW(a, P_ATOM)}`, prec: P_UNARY };
      if (t.f === 'deg') return { text: `${wrapW(a, P_ATOM)}°`, prec: P_POW };
      if (t.f === 'fact') return { text: `${wrapW(a, P_ATOM)}!`, prec: P_POW };
      if (t.f === 'abs') return { text: `|${a.text}|`, prec: P_ATOM };
      return { text: `${t.f}(${a.text})`, prec: P_UNARY };
    }
  }
}

/** Écriture d'un terme relue par la feuille (décimaux à virgule, « 18π », « 2√3 »). */
export function formulaText(t: Term): string {
  return showW(t).text;
}

/* ------------------------------ calculs sans grandeur ------------------------------ */
// Feuille « vérifier chaque affirmation » (moteur v36, lot 6.8) : des calculs sur des
// nombres seulement. Mêmes mesures que les maillons d'une feuille à formules, dans un
// monde vide (aucune grandeur, aucune ligne).

let constWorld: KForState | null = null;
function emptyWorld(): KForState {
  if (!constWorld)
    constWorld = {
      model: { symbols: {}, goal: ['_'] } as unknown as ScratchModel,
      goals: [],
      answer: null,
      W: {},
      Wf: {},
      given: [],
      tools: [],
      traps: [],
      checks: [],
      hyps: [],
      path: [],
      lines: [],
      checked: new Set(),
    };
  return constWorld;
}

/** Le genre d'un passage d'un membre sans grandeur à un autre. */
export type ConstStep = 'same' | 'reorder' | 'compute' | 'expand' | 'jump';

/**
 * Le passage du membre `a` au membre `b` (tous deux sans grandeur, de même valeur) :
 * `compute` (un calcul par endroit), `expand` (distribuer), `reorder` / `same` (rien de
 * nouveau), `jump` (plusieurs calculs d'un coup, ou une écriture qui recule).
 */
export function constStep(a: Term, b: Term): ConstStep {
  const ks = emptyWorld();
  const e = memberMove(ks, a, b);
  if (!e) return 'jump';
  const k = classify(ks, e, new Set());
  return k === 'replace' ? 'jump' : k;
}

/** Les deux membres ont-ils la même valeur ? null : indécis. */
export function constSameValue(a: Term, b: Term): boolean | null {
  return sameValue(emptyWorld(), a, b);
}

/** Une comparaison entre deux membres sans grandeur est-elle vraie ? null : indécis. */
export function constCmpHolds(rel: '>' | '<' | '≥' | '≤', a: Term, b: Term): boolean | null {
  return cmpHolds(emptyWorld(), rel, a, b);
}

/** Une valeur écrite sans calcul en attente (« 4 », « 7/3 », « 2√3 »). */
export function constIsValue(t: Term): boolean {
  return isValueForm(t);
}

/** Nombre d'opérations écrites. */
export function constOps(t: Term): number {
  return opsOf(t);
}

/** Clé de forme (sommes et produits triés, nombres tels qu'écrits). */
export function constShapeKey(t: Term): string {
  return shapeKey(t);
}

/**
 * Le calcul d'un membre sans grandeur, un tour à la fois (un calcul par endroit) : les
 * membres successifs, du terme donné à sa valeur (corrigé d'une feuille d'affirmations).
 */
export function constComputeChain(t: Term, max = 12): Term[] {
  const out = [t];
  let cur = t;
  for (let i = 0; i < max; i++) {
    const next = computeRound(cur);
    if (!next || shapeKey(next) === shapeKey(cur)) break;
    out.push(next);
    cur = next;
  }
  return out;
}

/** Une ligne de la feuille relue : ses membres, sa comparaison, sa valeur approchée. */
export function kforReadLine(
  text: string,
): { members: Term[]; cmp: '>' | '<' | '≥' | '≤' | null; approx: Term | null; line: Line } | null {
  return readText(text);
}

/** Même lecture, depuis les atomes de la ligne. */
export function kforReadAtoms(
  atoms: string[],
): { members: Term[]; cmp: '>' | '<' | '≥' | '≤' | null; approx: Term | null; line: Line } | null {
  try {
    return readAtoms(atoms);
  } catch {
    return null;
  }
}

/* ------------------------------ corrigé détaillé ------------------------------ */

/** Une valeur rationnelle comme terme écrit : décimal court, sinon fraction irréductible. */
function valueTerm(q: Q.Rat): Term {
  const w = ratWritten(q);
  if (!w.text.includes('/')) return num(q);
  // « −1/4 » se relit comme la fraction de −1 par 4
  return { k: 'div', a: num(Q.rat(q.n)), b: num(Q.rat(q.d)) };
}

/**
 * Un tour de calcul : chaque calcul « du bas » (opération sur des valeurs) est fait, un
 * seul par somme ou produit (les deux premiers nombres) ; des termes semblables réunis.
 * null : plus rien à calculer.
 */
function computeRound(t: Term): Term | null {
  const isVal = (x: Term) => ratOf(x) !== null || isAtomicValue(x) || x.k === 'sym';
  if (isPending(t)) {
    const kids: Term[] =
      t.k === 'add' || t.k === 'mul'
        ? t.c
        : t.k === 'fn'
          ? [t.a]
          : t.k === 'num' || t.k === 'sym'
            ? []
            : [t.a, t.b];
    // les opérandes sont des valeurs écrites (« cos(π ÷ 3) », « exp(ln(2)) », « π × 0 »)
    if (kids.every((k) => opsOf(k) === 0)) {
      if ((t.k === 'add' || t.k === 'mul') && t.c.length > 2) return combineTwo(t);
      const n = normalForm(t);
      const q = n.ok ? rfRat(n.rf) : null;
      return q ? valueTerm(q) : null;
    }
  }
  // « π/2 + 0 » → « π/2 », « x × 1 » → « x »
  if ((t.k === 'add' || t.k === 'mul') && !isNegation(t) && t.c.length >= 2) {
    const neutral = t.k === 'add' ? Q.ZERO : Q.ONE;
    const i = t.c.findIndex((c) => isNumT(c) && Q.eq(c.v, neutral));
    if (i >= 0) {
      const c = t.c.filter((_, k) => k !== i);
      return c.length === 1 ? c[0]! : { k: t.k, c };
    }
    if (t.k === 'mul' && t.c.some((c) => isNumT(c) && Q.isZero(c.v))) return num(0);
  }
  if ((t.k === 'add' || t.k === 'mul') && !isNegation(t)) {
    // dans un produit ou une somme qui reste irrationnel : deux nombres réunis
    const rats = t.c.filter((c) => ratOf(c) !== null);
    if (rats.length >= 2 && t.c.every(isVal)) return combineTwo(t);
    if (t.k === 'add') {
      const like = combineLike(t);
      if (like) return like;
    }
  }
  let changed = false;
  const map = (x: Term): Term => {
    const r = computeRound(x);
    if (r) {
      changed = true;
      return r;
    }
    return x;
  };
  // « −(3 + 5) » → « −8 » : le signe passe au nombre calculé
  if (isNegation(t)) {
    const inner = map((t as { c: Term[] }).c[1]!);
    // « −(−π ÷ 2) » → « π ÷ 2 »
    if (!changed) return irrationalRound(t);
    const q = ratOf(inner);
    return q && isNumT(inner) ? num(Q.neg(q)) : { k: 'mul', c: [num(-1), inner] };
  }
  let out: Term;
  switch (t.k) {
    case 'add':
    case 'mul':
      out = { k: t.k, c: t.c.map(map) };
      break;
    case 'div':
    case 'pow':
    case 'binom':
      out = { ...t, a: map(t.a), b: map(t.b) };
      break;
    case 'fn':
      out = { ...t, a: map(t.a) };
      break;
    default:
      return null;
  }
  return changed ? out : irrationalRound(t);
}

/**
 * Un calcul sur des valeurs irrationnelles, dont le résultat s'écrit avec une opération
 * de moins : « (π ÷ 3) ÷ 2 » → « π ÷ 6 », « π ÷ 6 × 36 » → « 6π », « 3 × ln(2) ÷ 3 » →
 * « ln(2) », « π ÷ 2 − (−π ÷ 2) » → « π ». null sinon.
 */
function irrationalRound(t: Term): Term | null {
  if (!isConstTerm(t) || isPending(t)) return null;
  const ops = opsOf(t);
  if (ops === 0) return null;
  // un nombre fois une seule valeur irrationnelle : « 3 × exp(−1) ÷ 3 » → « exp(−1) »
  const c = coefOf(t);
  if (c?.x) {
    const w = writeCoef(c.q, c.x);
    if (opsOf(w) === ops - 1 && shapeKey(w) !== shapeKey(t)) return w;
  }
  const n = normalForm(t);
  if (!n.ok) return null;
  let cand: Term;
  try {
    cand = niceValue(rfToTerm(n.rf));
  } catch {
    return null;
  }
  return opsOf(cand) === ops - 1 && shapeKey(cand) !== shapeKey(t) ? cand : null;
}

/**
 * Un produit (ou quotient) de nombres et d'UNE valeur irrationnelle : son coefficient et
 * cette valeur (« (π ÷ 3) ÷ 2 × 36 » : 6 et π). null sinon.
 */
function coefOf(t: Term): { q: Q.Rat; x: Term | null } | null {
  const r = ratOf(t);
  if (r !== null) return { q: r, x: null };
  if (isIrrationalAtom(t)) return { q: Q.ONE, x: t };
  if (isNegation(t)) {
    const c = coefOf((t as { c: Term[] }).c[1]!);
    return c ? { q: Q.neg(c.q), x: c.x } : null;
  }
  if (t.k === 'mul') {
    let q = Q.ONE;
    let x: Term | null = null;
    for (const f of t.c) {
      const c = coefOf(f);
      if (!c) return null;
      if (c.x) {
        if (x) return null;
        x = c.x;
      }
      q = Q.mul(q, c.q);
    }
    return { q, x };
  }
  if (t.k === 'div') {
    const a = coefOf(t.a);
    const b = ratOf(t.b);
    if (!a || b === null || Q.isZero(b)) return null;
    const q = Q.div(a.q, b);
    return q ? { q, x: a.x } : null;
  }
  return null;
}

/** « q × X » écrit comme l'élève l'écrit : « π ÷ 6 », « 6π », « −3√2 ÷ 4 », « exp(−1) ». */
function writeCoef(q: Q.Rat, x: Term | null): Term {
  if (!x) return valueTerm(q);
  if (Q.isZero(q)) return num(0);
  const neg = q.n < 0n;
  const n = neg ? -q.n : q.n;
  const base: Term = n === 1n ? x : { k: 'mul', c: [num(Q.rat(n)), x] };
  const v: Term = q.d === 1n ? base : { k: 'div', a: base, b: num(Q.rat(q.d)) };
  return neg ? { k: 'mul', c: [num(-1), v] } : v;
}

/** Une valeur du noyau écrite comme l'élève l'écrit : « (1/6)π » → « π ÷ 6 », « 6 × π » → « 6π ». */
function niceValue(t: Term): Term {
  if (t.k === 'add') return { k: 'add', c: t.c.map(niceValue) };
  if (t.k === 'mul' && t.c.length === 2 && !isNegation(t)) {
    const qi = t.c.findIndex((c) => isNumT(c));
    const q = qi >= 0 ? (t.c[qi] as { v: Q.Rat }).v : null;
    const x = qi >= 0 ? t.c[1 - qi]! : null;
    if (q && x && ratOf(x) === null) {
      const neg = q.n < 0n;
      const nAbs = neg ? -q.n : q.n;
      const base: Term = nAbs === 1n ? x : { k: 'mul', c: [num(Q.rat(nAbs)), x] };
      const v: Term = q.d === 1n ? base : { k: 'div', a: base, b: num(Q.rat(q.d)) };
      return neg ? { k: 'mul', c: [num(-1), v] } : v;
    }
  }
  return t;
}

/**
 * Deux opérandes d'une somme ou d'un produit réunis : les deux premiers nombres, sinon
 * deux valeurs dont le résultat est un nombre (« √3 × √3 » → 3).
 */
function combineTwo(t: { k: 'add' | 'mul'; c: Term[] }): Term | null {
  const pairs: [number, number][] = [];
  const rat = t.c.map((c) => ratOf(c) !== null);
  for (let i = 0; i < t.c.length; i++)
    for (let j = i + 1; j < t.c.length; j++) if (rat[i] && rat[j]) pairs.push([i, j]);
  for (let i = 0; i < t.c.length; i++)
    for (let j = i + 1; j < t.c.length; j++)
      if (!(rat[i] && rat[j]) && isConstTerm(t.c[i]!) && isConstTerm(t.c[j]!)) pairs.push([i, j]);
  for (const [i, j] of pairs) {
    const pair: Term = { k: t.k, c: [t.c[i]!, t.c[j]!] };
    const n = normalForm(pair);
    const q = n.ok ? rfRat(n.rf) : null;
    if (!q) continue;
    const c = t.c.filter((_, k) => k !== j).map((x, k) => (k === i ? valueTerm(q) : x));
    return c.length === 1 ? c[0]! : { k: t.k, c };
  }
  return null;
}

/** Deux termes semblables d'une somme réunis (« 5√2 + 3√2 » → « 8√2 »). */
function combineLike(t: { k: 'add'; c: Term[] }): Term | null {
  // « exp(2) ÷ 2 − exp(2) ÷ 4 » : deux multiples d'une même valeur irrationnelle
  for (let i = 0; i < t.c.length; i++)
    for (let j = i + 1; j < t.c.length; j++) {
      const p = coefOf(t.c[i]!);
      const q = coefOf(t.c[j]!);
      if (!p?.x || !q?.x || shapeKey(p.x) !== shapeKey(q.x)) continue;
      const merged = writeCoef(Q.add(p.q, q.q), p.x);
      const c = t.c.filter((_, x) => x !== j).map((x, y) => (y === i ? merged : x));
      return c.length === 1 ? c[0]! : { k: 'add', c };
    }
  const split = (x: Term): { k: Q.Rat; f: Term } | null => {
    if (x.k === 'mul' && x.c.length === 2 && !isNegation(x)) {
      const k = ratOf(x.c[0]!);
      if (k && ratOf(x.c[1]!) === null) return { k, f: x.c[1]! };
      const k2 = ratOf(x.c[1]!);
      if (k2 && ratOf(x.c[0]!) === null) return { k: k2, f: x.c[0]! };
    }
    if (isNegation(x)) {
      const inner = split((x as { c: Term[] }).c[1]!);
      return inner ? { k: Q.neg(inner.k), f: inner.f } : null;
    }
    if (ratOf(x) === null && x.k !== 'add') return { k: Q.ONE, f: x };
    return null;
  };
  for (let i = 0; i < t.c.length; i++)
    for (let j = i + 1; j < t.c.length; j++) {
      const p = split(t.c[i]!);
      const q = split(t.c[j]!);
      if (!p || !q || shapeKey(p.f) !== shapeKey(q.f)) continue;
      // « 2 × 1 × 1 + 2 × 1 × 1 » : deux calculs à faire, pas des termes semblables
      if (isPending(p.f)) continue;
      const k = Q.add(p.k, q.k);
      const merged: Term = Q.isZero(k)
        ? num(0)
        : Q.eq(k, Q.ONE)
          ? p.f
          : { k: 'mul', c: [valueTerm(k), p.f] };
      const c = t.c.filter((_, x) => x !== j).map((x, y) => (y === i ? merged : x));
      return c.length === 1 ? c[0]! : { k: 'add', c };
    }
  return null;
}

/** Remplace chaque grandeur connue par sa valeur écrite (énoncé ou ligne établie). */
function replaceKnown(ks: KForState, t: Term, known: Set<string>): Term | null {
  const map: Record<string, Term> = {};
  for (const n of symbolsOf(t)) {
    if (!known.has(n)) continue;
    const v = writtenValueOf(ks, n);
    if (v) map[n] = v;
  }
  if (!Object.keys(map).length) return null;
  return foldSigns(substitute(t, map));
}

/** « −F(0) » remplacé par 1 donne « −1 × 1 » : le signe passe au nombre (« −1 »). */
function foldSigns(t: Term): Term {
  switch (t.k) {
    case 'add':
    case 'mul': {
      const c = t.c.map(foldSigns);
      if (
        t.k === 'mul' &&
        c.length === 2 &&
        isNumT(c[0]!) &&
        Q.eq(c[0].v, Q.rat(-1)) &&
        isNumT(c[1]!)
      )
        return num(Q.neg(c[1].v));
      return { k: t.k, c };
    }
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: foldSigns(t.a), b: foldSigns(t.b) };
    case 'fn':
      return { ...t, a: foldSigns(t.a) };
    default:
      return t;
  }
}

function writtenValueOf(ks: KForState, n: string, depth = 0): Term | null {
  const all = facts(ks);
  for (const f of all) {
    if (f.l.k === 'sym' && f.l.n === n && isValueForm(f.r)) return f.r;
    if (f.r.k === 'sym' && f.r.n === n && isValueForm(f.l)) return f.l;
  }
  if (depth > 3) return null;
  for (const f of all) {
    if (f.l.k === 'sym' && f.l.n === n && f.r.k === 'sym') {
      const v = writtenValueOf(ks, f.r.n, depth + 1);
      if (v) return v;
    }
    if (f.r.k === 'sym' && f.r.n === n && f.l.k === 'sym') {
      const v = writtenValueOf(ks, f.l.n, depth + 1);
      if (v) return v;
    }
  }
  return null;
}

/**
 * Le corrigé du modèle, DÉTAILLÉ pour le noyau (conversion du lot 6.2) : une ligne qui
 * ferait plusieurs calculs d'un coup (« CM = 0,9 × 0,9 × 0,9 » → « CM = 0,729 ») est
 * précédée des lignes intermédiaires, une étape par ligne ; une ligne qu'on ne sait pas
 * rejoindre est laissée de côté (le calcul continue depuis la dernière ligne juste) ; une
 * réponse qui n'est pas sous sa forme finale est poursuivie. Rend null si une ligne du
 * corrigé est fausse ou s'éloigne de la réponse.
 */
export function refineFormulaPath(
  model: ScratchModel,
): { path: string[]; reached: boolean } | null {
  const ks = createKForState(model);
  const out: string[] = [];
  const chipsOf = (t: string) => kforLineAtoms(t).map((a) => ({ atoms: [a], text: a }));
  const play = (text: string): KForVerdict => kforJudge(ks, chipsOf(text), { verify: true });
  const accept = (text: string, v: KForVerdict): void => {
    kforCommit(ks, v);
    out.push(text);
  };
  const relText = (r: Rel2) => `${formulaText(r.l)} = ${formulaText(r.r)}`;
  /** Une étape de calcul sur une relation : remplacer les grandeurs connues, sinon calculer. */
  const advance = (r: Rel2): Rel2 | null => {
    const known = knownNames(ks);
    const rl = replaceKnown(ks, r.l, known);
    const rr = replaceKnown(ks, r.r, known);
    if (rl || rr) return { l: rl ?? r.l, r: rr ?? r.r };
    const cl = computeRound(r.l);
    const cr = computeRound(r.r);
    if (cl || cr) return { l: cl ?? r.l, r: cr ?? r.r };
    return null;
  };
  /**
   * Écrit la valeur d'une grandeur encore en calcul (« F(2) = 8 − 8/3 » → « F(2) = 16/3 »,
   * « q = y(1) ÷ 3 » → « q = 3 × exp(−1) ÷ 3 » → « q = exp(−1) »), depuis sa ligne la
   * plus récente (« 0,1 ÷ 0,3 = q » se lit aussi).
   */
  const complete = (names: Set<string>): void => {
    // les grandeurs intermédiaires avant la grandeur cherchée
    const order = [...names].sort((a, b) => +ks.goals.includes(a) - +ks.goals.includes(b));
    for (const n of order) {
      for (let guard = 0; guard < 10 && !knownNames(ks).has(n); guard++) {
        const f = facts(ks).find(
          (x) =>
            (x.l.k === 'sym' && x.l.n === n && !symbolsOf(x.r).has(n)) ||
            (x.r.k === 'sym' && x.r.n === n && !symbolsOf(x.l).has(n)),
        );
        if (!f) break;
        const expr = f.l.k === 'sym' && f.l.n === n ? f.r : f.l;
        const known = knownNames(ks);
        // remplacer quand toutes ses grandeurs sont connues (« I = −1 − 1 » d'un coup)
        const ready = [...symbolsOf(expr)].every((x) => known.has(x));
        const next = (ready ? replaceKnown(ks, expr, known) : null) ?? computeRound(expr);
        if (!next) break;
        const text = `${n} = ${formulaText(next)}`;
        const v = play(text);
        if (v.kind !== 'step') break;
        accept(text, v);
      }
    }
  };
  /** Pousse des lignes intermédiaires depuis `from` jusqu'à ce que `target` soit un pas. */
  const bridge = (from: Rel2, target: string): boolean => {
    let r = from;
    for (let round = 0; round < 14; round++) {
      const next = advance(r);
      if (!next) return false;
      r = next;
      const text = relText(r);
      const v = play(text);
      if (v.kind === 'dup') continue;
      if (v.kind !== 'step') return false;
      accept(text, v);
      const t = play(target);
      if (t.kind === 'step') {
        accept(target, t);
        return true;
      }
      if (t.kind === 'dup' || t.kind === 'given') return true;
    }
    return false;
  };
  for (const text of ks.path) {
    let v = play(text);
    if (v.kind === 'step') {
      accept(text, v);
      continue;
    }
    if (v.kind === 'dup' || v.kind === 'given') continue;
    if (!(v.kind === 'useless' && v.jump)) {
      // ligne fausse, ou qui recule : le corrigé n'est pas sûr
      if (v.kind === 'false' || v.kind === 'trap' || v.kind === 'syntax') return null;
      continue;
    }
    const F = readText(text);
    if (!F || F.members.length < 2) continue;
    // les grandeurs encore en calcul d'abord
    const names = new Set<string>();
    F.members.forEach((m) => symbolsOf(m).forEach((n) => names.add(n)));
    facts(ks).forEach((f) => {
      symbolsOf(f.r).forEach((n) => names.add(n));
    });
    complete(names);
    v = play(text);
    if (v.kind === 'step') {
      accept(text, v);
      continue;
    }
    // la ligne de confiance la plus récente qui mène à la ligne
    const target: Rel2 = { l: F.members[0]!, r: F.members[F.members.length - 1]! };
    // une formule de la carte pour cette grandeur, pas encore écrite : on l'écrit d'abord
    if (!facts(ks).some((f) => !f.given && shapeKey(f.l) === shapeKey(target.l))) {
      const tool = ks.tools.find(
        (tl, i) =>
          !ks.lines.some((l) => l.tool === i) &&
          (shapeKey(tl.rel.l) === shapeKey(target.l) || shapeKey(tl.rel.r) === shapeKey(target.l)),
      );
      if (tool) {
        const tv = play(tool.text);
        if (tv.kind === 'step') accept(tool.text, tv);
        const again = play(text);
        if (again.kind === 'step') {
          accept(text, again);
          continue;
        }
      }
    }
    const known = knownNames(ks);
    const src = facts(ks).find((f) => !f.given && relMove(ks, f, target, known)?.kind === 'jump');
    const head = facts(ks).find((f) => !f.given && shapeKey(f.l) === shapeKey(target.l));
    // sinon la ligne la plus récente qui parle de la même grandeur (« BC² = AB² + AC² »
    // avant « BC = √34 ») : on la calcule jusqu'à ce que la ligne devienne un pas
    const heads = symbolsOf(target.l);
    const related = facts(ks)
      .slice()
      .reverse()
      .find((f) => !f.given && [...symbolsOf(f.l), ...symbolsOf(f.r)].some((n) => heads.has(n)));
    const from = src ?? head ?? related;
    if (from) bridge({ l: from.l, r: from.r }, text);
  }
  // une réponse pas encore sous sa forme finale : on continue de calculer
  for (let guard = 0; guard < 16 && !kforGoalReached(ks); guard++) {
    complete(new Set(ks.goals));
    if (kforGoalReached(ks)) break;
    // les grandeurs de la ligne de la réponse d'abord (« I = exp(1) − F(0) » : F(0))
    const pending = facts(ks).find((f) => f.l.k === 'sym' && ks.goals.includes(f.l.n) && !f.given);
    if (pending) complete(symbolsOf(pending.r));
    if (kforGoalReached(ks)) break;
    const goalFact = facts(ks).find((f) => f.l.k === 'sym' && ks.goals.includes(f.l.n) && !f.given);
    if (!goalFact) break;
    const next = advance(goalFact);
    if (!next) break;
    const text = relText(next);
    const v = play(text);
    if (v.kind !== 'step') break;
    accept(text, v);
  }
  // une réponse déjà acceptée, mais à une opération près (« (π ÷ 6) × 36 ») : on finit le calcul
  for (let guard = 0; guard < 4 && kforGoalReached(ks); guard++) {
    const goalFact = facts(ks).find((f) => f.l.k === 'sym' && ks.goals.includes(f.l.n) && !f.given);
    if (!goalFact || !isConstTerm(goalFact.r)) break;
    const next = computeRound(goalFact.r);
    if (!next) break;
    const text = `${formulaText(goalFact.l)} = ${formulaText(next)}`;
    const v = play(text);
    if (v.kind !== 'step') break;
    accept(text, v);
  }
  // une réponse à arrondir : la valeur arrondie, écrite avec ses chiffres (« AB ≈ 5,0 »)
  const rg = roundingGoal(ks);
  if (!kforGoalReached(ks) && rg && ks.model.rounding) {
    const exact = facts(ks).find((f) => f.l.k === 'sym' && f.l.n === rg && isConstTerm(f.r));
    const x = exact ? floatOf(ks, exact.r) : NaN;
    if (Number.isFinite(x)) {
      const p = ks.model.rounding.places;
      const text = `${rg} ≈ ${formatNumber(roundTo(x, p))}`;
      const v = play(text);
      if (v.kind === 'step') accept(text, v);
    }
  }
  return { path: out, reached: kforGoalReached(ks) };
}
